// useBeaconCore.js
//
// Shared Magic Beacon brain — extracted from BeaconMultiViewDisplay.jsx so
// the same logic can be consumed by both BeaconMultiViewDisplay (regular
// web/mobile) and BeaconMultiViewTV (Android TV variant). Holds every bit
// of Beacon state, polling effects, action functions, BLE wrappers, and
// the `window.beacon*` dev helpers. The consumer (Display or TV) owns:
//
//   - multiview-shape state: videoIds, currentVideoIds, selectedIndex,
//     fullscreenIndex, allThumbnails, endedIndexes, recentlyEndedIds
//   - liveStreams autofill effects (deciding what to show when nothing
//     is locked) — Display-shape concern, not a Beacon concern
//   - all JSX render output
//   - the <SyncFullscreen> mount, wiring props from the hook return
//
// The hook receives `liveStreams`, `videoIds`, `selectedIndex`,
// `fullscreenIndex`, `isTv`, and the setters it needs to mutate slot
// contents (`setVideoIds`, `setCurrentVideoIds`, `setFullscreenIndex`).
// It derives `videoIdToStreamer` internally from `liveStreams` so the
// consumer can't accidentally pass a stale map.
//
// Why setters cross the boundary: assignToSlot (picker tile tap → enter
// sync for a streamer not yet in a slot) mutates videoIds, and Display
// owns videoIds because the liveStreams autofill needs to read+write it.
// Moving videoIds into the hook would drag the entire autofill effect
// (and liveStreams plumbing) into the hook, bloating it with non-Beacon
// concerns. Setter injection is the cleaner split.
//
// setFullscreenIndex is injected so exitSyncMode can return the user to
// the embed grid view when they tap EXIT inside an auto-promoted HLS
// session. Without this, leaving HLS lands the user back in the
// fullscreen embed view, which is confusing because they don't realize
// the HLS layer was just unmounted (they think they're still synced).
// Mirrors the Auto Unlock UX: exit means "back to grid."

import { useEffect, useRef, useState, useCallback } from "react";
import axios from "../../../config/axios";
import useBeaconControl from "../../../hooks/useBeaconControl";

const BEACON_API_BASE = "https://beacon.mywaitserver.com";

const POLL_ROSTER_MS = 5000;
const POLL_BEACON_SYNC_MS = 2000;
const POLL_SCHEDULE_MS = 60_000;
// 5a-ii: listener window + monitor + eligibility polling cadences,
// matching multitest2 (line 5378, 5408, 5424).
const POLL_LISTENER_WINDOW_MS = 15_000;
const POLL_MONITOR_ENABLED_MS = 15_000;
const POLL_ELIGIBLE_STREAMS_MS = 5_000;

const SYNC_LOCK_POLL_MS = 2000;
const GRACE_TIMEOUT_MS = 120_000;
const WAS_NEAR_END_THRESHOLD_S = 30;
const ANCHOR_BACKWARD_RESET_S = 30;
const NULL_POLL_TOLERANCE = 3;

// 4g: localStorage keys mirror multitest2 line 3196.
const LS_HLS_VOL = "mb_vol_hls";
const LS_HLS_MUTE = "mb_vol_hls_muted";
const LS_BEACON_USER_ENABLED = "mb_beacon_user_enabled";
const LS_TINK_USER_ENABLED = "mb_tink_user_enabled";

const VOL_STEP = 10;

// ============================================================
// 5a-i: mb-status widget constants & helpers
// ============================================================
const MB_TICK_MS = 1000;
// 5 min before showtime → switch from standby copy to live countdown.
// Matches multitest2 line 5334.
const MB_COUNTDOWN_THRESHOLD_MS = 330_000;
const MB_COUNTDOWN_URGENT_THRESHOLD_MS = 10_000;
// 5a-iii: cosmetic 10s offset so countdown hits 0:00 at showtime+10s
// (matches YouTube live broadcast lag). Multitest2 line 5817.
const MB_COUNTDOWN_DISPLAY_OFFSET_MS = 30_000;
// Past-showtime grace: keep the schedule entry "live" for this long
// past start so a show that's underway counts as a future schedule.
const MB_PAST_SHOWTIME_GRACE_MS = 5 * 60 * 1000;
// 5a-iii: keep a post-show event in the relevant set for this long
// past its scheduled end, so a brief unlock at the tail doesn't drop
// the row before the next render. Multitest2 uses ~15s.
const MB_POSTSHOW_HIDE_GRACE_MS = 15_000;
// 5a-iii: same-start consolidation — events whose startMs differs by
// less than this fold into a single row ("Luminous & Fantasmic: ...").
// Multitest2 line ~6090.
const MB_CONSOLIDATE_WINDOW_MS = 60_000;

// Per-show duration. Mirrors multitest2 line 5329. Used to determine
// whether a show is still "happening" (i.e., a future schedule exists)
// so the widget keeps surfacing standby copy through the show.
const MB_SHOW_DURATION_SEC = {
  "happily-ever-after": 1700,
  luminous: 1004,
  fantasmic: 1700,
  "fantasmic-dl": 1700,
  "wondrous-journeys": 1100,
  "world-of-color": 1500,
  "not-so-spooky-spectacular": 1500,
  "xmas-mk": 1700,
};

// 5a-iii: syncing-phase durations per show. Phase 1 is the initial
// "Syncing to show" countdown; phase 2 is the looping "Taking a moment
// longer..." until lock or show-end. Multitest2 line 5339-5343.
const MB_SYNCING_PHASES_DEFAULT = {
  initialMs: 2 * 60 * 1000,
  extendedMs: 1 * 60 * 1000,
};
const MB_SYNCING_PHASES_PER_SHOW = {
  luminous: { initialMs: 5 * 60 * 1000, extendedMs: 2 * 60 * 1000 },
};
function getSyncingPhases(showId) {
  return MB_SYNCING_PHASES_PER_SHOW[showId] || MB_SYNCING_PHASES_DEFAULT;
}

// 5a-iii: per-show timezone for "Up Next: Show @ 9:00 PM ET" rendering.
// Mirrors multitest2 line 5803.
const MB_SHOW_TZ = {
  "happily-ever-after": { tz: "America/New_York", label: "ET" },
  luminous: { tz: "America/New_York", label: "ET" },
  fantasmic: { tz: "America/New_York", label: "ET" },
  "not-so-spooky-spectacular": { tz: "America/New_York", label: "ET" },
  "xmas-mk": { tz: "America/New_York", label: "ET" },
  "wondrous-journeys": { tz: "America/Los_Angeles", label: "PT" },
  "world-of-color": { tz: "America/Los_Angeles", label: "PT" },
  "fantasmic-dl": { tz: "America/Los_Angeles", label: "PT" },
};

const MB_PRETTY_SHOW = {
  "happily-ever-after": "Happily Ever After",
  luminous: "Luminous",
  fantasmic: "Fantasmic",
  "fantasmic-dl": "Fantasmic",
  "wondrous-journeys": "Wondrous Journeys",
  "world-of-color": "World of Color",
  "not-so-spooky-spectacular": "MK Halloween Show",
  "xmas-mk": "MK Xmas Show",
};

// Exported so Display/TV JSX can render row/badge labels in the same
// pretty form (e.g. "Now Playing: Happily Ever After").
export function prettyShow(showId) {
  if (!showId) return "—";
  return (
    MB_PRETTY_SHOW[showId] ||
    showId
      .split("-")
      .map((s) => s.charAt(0).toUpperCase() + s.slice(1))
      .join(" ")
  );
}

// Exported so Display/TV JSX can render the same countdown strings as
// the hook computes for badge state.
export function formatCountdown(ms) {
  const totalSec = Math.max(0, Math.floor(ms / 1000));
  const m = Math.floor(totalSec / 60);
  const s = totalSec % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
}

// 5a-iii: format a show's date in its native park time zone (WDW → ET,
// DL → PT). Used by the "Up Next" row. Mirrors multitest2 line 5817.
function formatShowTime(date, showId) {
  const z = MB_SHOW_TZ[showId] || { tz: "America/New_York", label: "ET" };
  const opts = {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
    timeZone: z.tz,
  };
  return date.toLocaleTimeString("en-US", opts) + " " + z.label;
}

// 5a-iii: turn a list of pretty show names into "A & B" or "A, B & C".
function joinShowNames(names) {
  if (names.length === 0) return "";
  if (names.length === 1) return names[0];
  if (names.length === 2) return `${names[0]} & ${names[1]}`;
  return names.slice(0, -1).join(", ") + " & " + names[names.length - 1];
}

// 5a-iv: derive {startMs, endMs} for tonight's listener window from the
// HH:MM strings on listenerWindow.{start,end}. Multitest2 line 5477.
// Returns null if no window data, else absolute ms timestamps of the
// upcoming or current window in ET. Handles end<=start as overnight
// crossing (e.g. 19:00 → 03:00 next morning).
function computeListenerWindowRange(lw) {
  if (!lw || !lw.start || !lw.end) return null;
  const [sH, sM] = lw.start.split(":").map(Number);
  const [eH, eM] = lw.end.split(":").map(Number);
  if (isNaN(sH) || isNaN(sM) || isNaN(eH) || isNaN(eM)) return null;
  const now = new Date();
  const todayET = new Date(
    now.toLocaleString("en-US", { timeZone: "America/New_York" })
  );
  const startToday = new Date(todayET);
  startToday.setHours(sH, sM, 0, 0);
  const endTodaySameDay = new Date(todayET);
  endTodaySameDay.setHours(eH, eM, 0, 0);
  const crossesMidnight =
    endTodaySameDay.getTime() <= startToday.getTime();
  const endIfStartedToday = crossesMidnight
    ? new Date(endTodaySameDay.getTime() + 24 * 60 * 60 * 1000)
    : endTodaySameDay;
  const tzOffsetDelta = todayET.getTime() - now.getTime();
  // Currently inside today's window?
  if (
    todayET.getTime() >= startToday.getTime() &&
    todayET.getTime() <= endIfStartedToday.getTime()
  ) {
    return {
      startMs: startToday.getTime() - tzOffsetDelta,
      endMs: endIfStartedToday.getTime() - tzOffsetDelta,
    };
  }
  // Past today's window → next window is +24h.
  if (todayET.getTime() > endIfStartedToday.getTime()) {
    return {
      startMs: startToday.getTime() + 24 * 60 * 60 * 1000 - tzOffsetDelta,
      endMs:
        endIfStartedToday.getTime() + 24 * 60 * 60 * 1000 - tzOffsetDelta,
    };
  }
  // Before today's window → return TONIGHT's upcoming window
  // (multitest2 v8.8.169 fix: morning-of, return upcoming-tonight, not
  // yesterday's already-ended window).
  return {
    startMs: startToday.getTime() - tzOffsetDelta,
    endMs: endIfStartedToday.getTime() - tzOffsetDelta,
  };
}

// ============================================================
// BLE transport (4a)
// ============================================================
//
// NOTE: these are the RAW transport functions. Internal callers in this
// hook must NOT use them directly — instead they call gatedSendBeaconCmd
// / gatedSendCancel (defined inside the hook body) which add an
// isController check on top. The raw functions are still used by the
// gated wrappers themselves, and exported via the hook return so
// SyncFullscreen / multiview wiring gets the gated versions transparently.

function sendBeaconCmd(deviceId, fx, vib) {
  if (!deviceId) {
    console.warn("[beacon] BLE skipped — no deviceId:", { fx, vib });
    return Promise.resolve();
  }
  return axios
    .post(
      "/esp/beacon-show",
      { deviceId, fx, vib },
      { withCredentials: true }
    )
    .catch((err) => {
      console.error("[beacon] BLE command failed:", { fx, vib }, err);
    });
}

function sendCancel(deviceId) {
  if (!deviceId) {
    console.warn("[beacon] BLE CANCEL skipped — no deviceId");
    return Promise.resolve();
  }
  return axios
    .post("/esp/cancel", { deviceId }, { withCredentials: true })
    .catch((err) => {
      console.error("[beacon] BLE cancel failed:", err);
    });
}

// ============================================================
// CSV cue loading (4b)
// ============================================================

function parseCueCsv(text) {
  const lines = text.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
  const cues = [];
  for (const line of lines) {
    if (line.startsWith("#")) continue;
    const parts = line.split(",").map((p) => p.trim());
    const tStr = parts[0];
    if (!tStr) continue;
    let tMs;
    if (tStr.includes(":")) {
      const segs = tStr.split(":").map(parseFloat);
      if (segs.some((n) => !Number.isFinite(n))) continue;
      let totalSec = 0;
      if (segs.length === 2) totalSec = segs[0] * 60 + segs[1];
      else if (segs.length === 3)
        totalSec = segs[0] * 3600 + segs[1] * 60 + segs[2];
      else continue;
      tMs = totalSec * 1000;
    } else {
      const n = parseFloat(tStr);
      if (!Number.isFinite(n)) continue;
      tMs = n < 10000 ? n * 1000 : n;
    }
    if (!parts[1]) continue;
    cues.push({
      tMs,
      fx: parts[1],
      vib: parts[2] || "OFF",
      raw: line,
    });
  }
  cues.sort((a, b) => a.tMs - b.tMs);
  return cues;
}

// ============================================================
// localStorage helpers (4g)
// ============================================================

function lsGetNum(key, fallback) {
  try {
    const v = localStorage.getItem(key);
    if (v == null) return fallback;
    const n = parseInt(v, 10);
    return Number.isFinite(n) ? n : fallback;
  } catch {
    return fallback;
  }
}

function lsGetBool(key, fallback) {
  try {
    const v = localStorage.getItem(key);
    if (v == null) return fallback;
    return v === "1" || v === "true";
  } catch {
    return fallback;
  }
}

function lsSet(key, value) {
  try {
    localStorage.setItem(key, String(value));
  } catch {}
}

// ============================================================

// Exported so the consumer's picker JSX can derive YouTube thumbnails
// from a stream's youtubeUrl without duplicating the regex.
export function extractYouTubeId(url) {
  if (!url) return null;
  const m = url.match(/(?:v=|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
  return m ? m[1] : null;
}

// ============================================================
// THE HOOK
// ============================================================

export default function useBeaconCore({
  // Multiview-shape state owned by the consumer
  liveStreams,
  videoIds,
  selectedIndex,
  fullscreenIndex,
  // Boolean: TV variant vs Display variant. Drives HLS resolution
  // ladder selection (isTv=1 → 1080p target, isTv=0 → 720p).
  isTv,
  // Setters the hook needs to call:
  setVideoIds,
  setCurrentVideoIds,
  // setFullscreenIndex — called by exitSyncMode to return the user to
  // the grid view when they exit HLS. Optional for backward compat: if
  // the consumer doesn't pass it, the hook just won't clear fullscreen
  // on exit (legacy behavior). Display + TV both wire it.
  setFullscreenIndex,
  // Base-app integrations the hook reads through:
  beaconDeviceId, // from useGlobalStore — device.magic_beacon_device._id
  // Embed-volume controls — only used when sync is INACTIVE.
  // (When sync is active, vol+/vol-/mute route to HLS volume instead.)
  isMuted,
  toggleMute,
  increaseVolume,
  decreaseVolume,
}) {
  // Derive videoIdToStreamer from liveStreams on every render. The hook
  // owns this map so action closures (picker tile tap, peer handoff,
  // resolveStreamId fallback) always read the freshest value without
  // Display having to remember to recompute + re-pass it as a prop.
  // Identity churns each render but that's fine — none of our effect
  // dep arrays include it (they depend on liveStreams instead).
  const videoIdToStreamer = Object.entries(liveStreams || {}).reduce(
    (acc, [streamer, data]) => {
      if (data?.videoId) acc[data.videoId] = data.name || streamer;
      return acc;
    },
    {}
  );

  // ===== State =====

  const [streamIdByVideoId, setStreamIdByVideoId] = useState({});
  const [streamIdByName, setStreamIdByName] = useState({});
  // 4h: full roster (array of stream meta objects) so the picker can
  // render tiles for streams not currently in any slot. Each entry has
  // at minimum: { streamId, name, youtubeUrl }.
  const [roster, setRoster] = useState([]);
  const [globalAnchors, setGlobalAnchors] = useState({});
  const [fakeLocks, setFakeLocks] = useState({});

  const [syncMode, setSyncMode] = useState({
    active: false,
    streamId: null,
    anchor: null,
    earlyEntry: false,
    earlyEntryShowId: null,
    showtimeMs: null,
  });

  const [cuesByShow, setCuesByShow] = useState({});
  const [showSchedule, setShowSchedule] = useState({});
  const [graceModeActive, setGraceModeActive] = useState(false);

  // 5a-ii: listener-window, monitor-enabled (per-show dashboard toggle),
  // and eligible-streams maps. All polled in the background. Drive the
  // refined mb-status state machine. Mirrors multitest2 line 5390/5402/5379.
  const [listenerWindow, setListenerWindow] = useState(null);
  const [monitorEnabled, setMonitorEnabled] = useState({});
  const [eligibleStreams, setEligibleStreams] = useState({});

  // 5a-i + 5a-iii: mb-status widget state. Row-based shape.
  // Shape: { topState, lockedRows: [...], nonLockedRows: [...] }
  const [mbStatus, setMbStatus] = useState({
    topState: "hidden",
    lockedRows: [],
    nonLockedRows: [],
  });

  // 5a-iv: mb-info-strip state. Slim band between the embed grid and
  // the thumbnail scroll, driven by a 1Hz tick that mirrors multitest2
  // mbInfoStripTick (line 6327).
  const [infoStrip, setInfoStrip] = useState({
    visible: false,
    prefix: "",
    show: "",
    timeStr: null,
    countdownStr: null,
  });

  // 5f: embed-fullscreen in-slot badge state, keyed by slot index. Each
  // entry: { phase, showId, showName, showTimeStr, detail, remaining,
  // tappable, label, peerId } or null. Driven by a 1Hz tick that mirrors
  // multitest2 decorateSlot's fs-mb-status block (line 2955).
  const [fsBadges, setFsBadges] = useState({});

  // Guard for the auto-promote: tracks which (slotIdx:showId) pairs
  // have already fired their auto-enter so we don't re-launch HLS every
  // tick the user is fullscreened on an active phase.
  //
  // CRITICAL: when EITHER phase (countdown or locked) fires an auto-
  // promote, BOTH guard keys are pre-added so the other phase can't
  // re-promote after a user exit. Without this, the user-exits-HLS-loop
  // bug manifests as follows:
  //
  //   1. User fullscreen during countdown phase → countdown auto-
  //      promote fires, sets guard "idx:showId"
  //   2. Lock arrives mid-HLS, user sees green badge
  //   3. User presses EXIT → exitSyncMode → syncMode.active=false
  //   4. Next fsBadges tick: ctx.phase is now "locked"
  //   5. Locked branch checks key "idx:showId:locked" — NOT FOUND
  //      (only the countdown key was set)
  //   6. Locked auto-promote fires → user trapped in HLS loop
  //
  // The fix: each branch pre-adds the OTHER branch's guard key so any
  // auto-promote on a (slot, show) blocks all future auto-promotes for
  // that pair, regardless of phase changes.
  //
  // Key formats:
  //   "<idx>:<showId>"          — countdown phase guard
  //   "<idx>:<showId>:locked"   — locked phase guard
  const autoPromoteFiredRef = useRef(new Set());
  // Diagnostic: dedupes repeated "fullscreen but no badge" log lines so
  // we only emit one warning per (slot, stream, phase) change. Used by
  // the BADGE DIAGNOSTIC block in the fsBadges compute effect below.
  const badgeDiagLastKeyRef = useRef(null);

  // 4g: HLS volume state (separate from Zustand embed volume).
  const [hlsVolume, setHlsVolume] = useState(() => lsGetNum(LS_HLS_VOL, 70));
  const [hlsMuted, setHlsMuted] = useState(() => lsGetBool(LS_HLS_MUTE, false));

  // 4g: Beacon + Tink user toggles.
  const [beaconUserEnabled, setBeaconUserEnabled] = useState(() =>
    lsGetBool(LS_BEACON_USER_ENABLED, true)
  );
  const [tinkUserEnabled, setTinkUserEnabled] = useState(() =>
    lsGetBool(LS_TINK_USER_ENABLED, true)
  );

  // 4g: gear-expanded state mirrors multitest2 #volBar.gear-expanded.
  // Default collapsed — only X + gear visible; tap gear to reveal cluster.
  const [gearExpanded, setGearExpanded] = useState(false);

  // 4h: stream picker modal state. Mirrors multitest2 line 7895-7899.
  const [streamPicker, setStreamPicker] = useState({
    open: false,
    items: [],
    title: "",
    mode: "locked",
    showId: null,
    showIds: null,
    openedAt: 0,
  });

  // ===== Refs =====

  const currentAnchorRef = useRef(null);
  const fakeLocksRef = useRef({});
  const beaconDeviceIdRef = useRef(null);
  const lastShowPosRef = useRef(null);

  // 5a-iii: per-show timestamp of when the "syncing" phase began.
  // Mirrors multitest2's mbSyncingStartedAt module-level dict.
  const mbSyncingStartedAtRef = useRef({});

  // 4f: pre-show wake scheduler state. Mirrors multitest2 line 4366/4374.
  const wakeSent60Ref = useRef({});
  const wakeSent30Ref = useRef({});
  const pendingWakeTimeoutsRef = useRef([]);

  const syncSessionRef = useRef({
    consecutiveNullPolls: 0,
    graceModeStartedAt: 0,
    graceModeLastShowPos: null,
    graceModeReferenceDuration: null,
  });

  // 5b: peer-handoff staging ref. Carries pending handoff info across
  // the sync-active flip so flushPeerHandoff can complete the handoff
  // once syncMode.active settles to false.
  const peerHandoffPendingRef = useRef(null);

  // ===== Ref-sync effects =====
  useEffect(() => {
    currentAnchorRef.current = syncMode.anchor;
  }, [syncMode.anchor]);
  useEffect(() => {
    fakeLocksRef.current = fakeLocks;
  }, [fakeLocks]);
  useEffect(() => {
    beaconDeviceIdRef.current = beaconDeviceId;
  }, [beaconDeviceId]);

  // ===== Multi-device beacon control gate =====
  //
  // Every BLE fire from this hook routes through gatedSendBeaconCmd /
  // gatedSendCancel. When isController is false (because this device
  // doesn't have the Magic Beacon addon, or another device on the same
  // account currently holds the lock), the gated wrappers no-op.
  //
  // Ref pattern is required: the long-running intervals here (anchor
  // poller @ 2s, wake scheduler @ 1s, fsBadges tick @ 1s) close over the
  // captured isController at the moment the effect mounted. Without the
  // ref, a user becoming inactive MID-SESSION would still see commands
  // fire from a stale captured closure until the next dep-array change
  // re-mounted the effect. The ref reads fresh on every tick.
  //
  // Also exposed via the hook return as sendBeaconCmd / sendCancel so
  // SyncFullscreen + any other downstream wiring inherits the gate.
  const {
    isController,
    hasHolder,
    holder,
    synced: controlSynced,
    keyReady: controlKeyReady,
    claim: claimControl,
  } = useBeaconControl();
  const isControllerRef = useRef(isController);
  useEffect(() => {
    isControllerRef.current = isController;
  }, [isController]);

  const gatedSendBeaconCmd = useCallback((deviceId, fx, vib) => {
    if (!isControllerRef.current) return Promise.resolve();
    return sendBeaconCmd(deviceId, fx, vib);
  }, []);
  const gatedSendCancel = useCallback((deviceId) => {
    if (!isControllerRef.current) return Promise.resolve();
    return sendCancel(deviceId);
  }, []);

  // ===== Roster polling (3a) =====
  useEffect(() => {
    let cancelled = false;
    const fetchRoster = async () => {
      try {
        const r = await fetch(`${BEACON_API_BASE}/api/roster`);
        if (!r.ok) return;
        const data = await r.json();
        if (cancelled) return;
        const indexById = {};
        const indexByName = {};
        const rosterArr = [];
        for (const s of Object.values(data.streams || {})) {
          if (!s.streamId) continue;
          rosterArr.push(s);
          if (s.youtubeUrl) {
            const vid = extractYouTubeId(s.youtubeUrl);
            if (vid) indexById[vid] = s.streamId;
          }
          if (s.name) {
            indexByName[s.name.toLowerCase()] = s.streamId;
          }
        }
        setStreamIdByVideoId(indexById);
        setStreamIdByName(indexByName);
        setRoster(rosterArr);
      } catch (e) {
        console.warn("[beacon] roster fetch failed:", e.message);
      }
    };
    fetchRoster();
    const id = setInterval(fetchRoster, POLL_ROSTER_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, []);

  // ===== Global anchors polling (3a) =====
  useEffect(() => {
    let cancelled = false;
    const fetchAnchors = async () => {
      try {
        const r = await fetch(`${BEACON_API_BASE}/api/beacon-sync`);
        if (!r.ok) return;
        const data = await r.json();
        if (cancelled) return;
        setGlobalAnchors(data.beaconSync || {});
      } catch (e) {
        console.warn("[beacon] beacon-sync fetch failed:", e.message);
      }
    };
    fetchAnchors();
    const id = setInterval(fetchAnchors, POLL_BEACON_SYNC_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, []);

  // ===== Schedule polling (4e) =====
  useEffect(() => {
    let cancelled = false;
    const fetchSchedule = async () => {
      try {
        const r = await fetch(`${BEACON_API_BASE}/api/schedule`);
        if (!r.ok) return;
        const raw = await r.json();
        if (cancelled) return;
        const out = {};
        for (const [showId, val] of Object.entries(raw || {})) {
          if (!val) continue;
          const arr = Array.isArray(val) ? val : [val];
          out[showId] = arr.map((s) => new Date(s)).filter(
            (d) => !isNaN(d.getTime())
          );
        }
        setShowSchedule(out);
      } catch (e) {
        console.warn("[beacon] schedule fetch failed:", e.message);
      }
    };
    fetchSchedule();
    const id = setInterval(fetchSchedule, POLL_SCHEDULE_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, []);

  // ===== 5a-ii: listener-window polling (15s) — multitest2 line 5408. =====
  // Response shape: { start: "07:00", end: "03:00", isActive, enabled, tz }
  useEffect(() => {
    let cancelled = false;
    const fetchLW = async () => {
      try {
        const r = await fetch(`${BEACON_API_BASE}/api/listener-window`);
        if (!r.ok) return;
        const j = await r.json();
        if (cancelled) return;
        setListenerWindow(j || null);
      } catch (e) {
        console.warn("[beacon] listener-window fetch failed:", e.message);
      }
    };
    fetchLW();
    const id = setInterval(fetchLW, POLL_LISTENER_WINDOW_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, []);

  // ===== 5a-ii: monitor-enabled polling (15s) — multitest2 line 5424. =====
  // Only DISABLED shows appear in the map (value=false). Any show not
  // in the map is enabled by default.
  useEffect(() => {
    let cancelled = false;
    const fetchME = async () => {
      try {
        const r = await fetch(`${BEACON_API_BASE}/api/monitor-enabled`);
        if (!r.ok) return;
        const j = await r.json();
        if (cancelled) return;
        setMonitorEnabled(j || {});
      } catch (e) {
        console.warn("[beacon] monitor-enabled fetch failed:", e.message);
      }
    };
    fetchME();
    const id = setInterval(fetchME, POLL_MONITOR_ENABLED_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, []);

  // ===== 5a-ii: eligible-streams polling (5s) — multitest2 line 5379. =====
  // Map of streamId → { eligibleForShows: [showId, ...], ... }.
  useEffect(() => {
    let cancelled = false;
    const fetchES = async () => {
      try {
        const r = await fetch(`${BEACON_API_BASE}/api/eligible-streams`);
        if (!r.ok) return;
        const j = await r.json();
        if (cancelled) return;
        setEligibleStreams(j?.eligibleStreams || {});
      } catch (e) {
        console.warn("[beacon] eligible-streams fetch failed:", e.message);
      }
    };
    fetchES();
    const id = setInterval(fetchES, POLL_ELIGIBLE_STREAMS_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, []);

  // ===== 5a-i + 5a-ii + 5a-iii: mb-status widget state machine =====
  //
  // 5a-iii rewrite: returns two row arrays instead of a single state.
  // Mirrors multitest2 mbRender's lockedRows/nonLockedRows model.
  //
  //   lockedRows    — green pulse, clickable. "Show: ✨ Connected! TAP
  //                   HERE TO SYNC NOW!" Tap enters HLS sync for that
  //                   stream's slot.
  //   nonLockedRows — purple. May contain:
  //                   - syncing      "Show: Syncing to show 1:30"  (passive)
  //                   - countdown-cta "Show: TAP HERE TO SYNC NOW!" (early
  //                                    entry, clickable when eligible
  //                                    streamer exists)
  //                   - countdown-preview "Show starts in M:SS"     (passive)
  //                   - upcoming     "Up Next: Show @ 9:00 PM ET"  (passive)
  //                   - standby      "Tap Here 5 Min Before Showtime" /
  //                                  "Searching for streamers..." /
  //                                  "Looking for streamers..."     (passive)
  //                   - closed       "Opens at 7:00 PM ET"          (passive)
  //
  // When both row buckets exist, the first nonLockedRow gets a
  // `mb-row-pending-after-locked` class for visual separation.
  // Multitest2 line 6288.
  useEffect(() => {
    // ---- Helpers (closure over current state) ----
    const isShowEnabled = (showId) => monitorEnabled[showId] !== false;

    const formatListenerWindowStart = () => {
      const lw = listenerWindow;
      if (!lw) return "";
      const startStr = lw.start || "";
      const m = startStr.match(/^(\d{1,2}):(\d{2})$/);
      if (!m) return "";
      let h = Number(m[1]);
      const mins = m[2];
      const tz = lw.tz || "America/New_York";
      const ampm = h >= 12 ? "PM" : "AM";
      if (h === 0) h = 12;
      else if (h > 12) h -= 12;
      let tzLabel = "ET";
      if (tz.includes("Los_Angeles")) tzLabel = "PT";
      else if (tz.includes("Chicago")) tzLabel = "CT";
      else if (tz.includes("Denver")) tzLabel = "MT";
      return `${h}:${mins} ${ampm} ${tzLabel}`;
    };

    // showId → array of streamIds eligible to broadcast for that show.
    const eligibleStreamIdsForShow = (showId) => {
      const out = [];
      for (const [streamId, info] of Object.entries(eligibleStreams || {})) {
        const list = info?.eligibleForShows;
        if (Array.isArray(list) && list.includes(showId)) out.push(streamId);
      }
      return out;
    };

    const isPastFirstShowOfWindow = () => {
      const lw = listenerWindow;
      if (!lw || !lw.isActive) return false;
      const now = Date.now();
      let firstStartMs = null;
      for (const [showId, dates] of Object.entries(showSchedule || {})) {
        if (!isShowEnabled(showId)) continue;
        if (!Array.isArray(dates)) continue;
        for (const d of dates) {
          const ms = d.getTime();
          if (Math.abs(ms - now) > 24 * 60 * 60 * 1000) continue;
          if (firstStartMs == null || ms < firstStartMs) firstStartMs = ms;
        }
      }
      if (firstStartMs == null) return false;
      return now >= firstStartMs;
    };

    // ---- mbComputeRelevantEvents equivalent. Multitest2 line 5681. ----
    // For each scheduled show, emit one event per scheduled showtime
    // with phase classification fields. Active locks override staleness.
    const computeRelevantEvents = (now) => {
      const allLocks = { ...globalAnchors, ...fakeLocks };
      const events = [];
      for (const [showId, dates] of Object.entries(showSchedule || {})) {
        if (!isShowEnabled(showId)) continue;
        if (!Array.isArray(dates)) continue;
        const durationSec = MB_SHOW_DURATION_SEC[showId] || 1800;

        // Find which scheduled startMs (if any) matches an active anchor
        // for this show. Multitest2 line 5697-5722 — the inferred start
        // is anchorPDTMs - anchorShowPosSec*1000; pick the closest
        // scheduled startMs to that.
        let bestLockShowtime = null;
        let bestLockStreamId = null;
        for (const [sid, anchor] of Object.entries(allLocks)) {
          if (!anchor || anchor.showId !== showId) continue;
          const inferredStart =
            (anchor.anchorPDTMs || 0) - (anchor.anchorShowPosSec || 0) * 1000;
          let closestStart = null;
          let closestDelta = Infinity;
          for (const d of dates) {
            const delta = Math.abs(d.getTime() - inferredStart);
            if (delta < closestDelta) {
              closestDelta = delta;
              closestStart = d.getTime();
            }
          }
          if (closestStart != null) {
            bestLockShowtime = closestStart;
            bestLockStreamId = sid;
            break;
          }
        }

        const hasEligible = eligibleStreamIdsForShow(showId).length > 0;
        for (const d of dates) {
          const startMs = d.getTime();
          const endMs = startMs + durationSec * 1000;
          const isLocked =
            bestLockShowtime != null && startMs === bestLockShowtime;
          const lockedStreamId = isLocked ? bestLockStreamId : null;
          // Drop if past end+grace AND not currently locked.
          if (!isLocked && now > endMs + MB_POSTSHOW_HIDE_GRACE_MS) continue;
          events.push({
            showId,
            startMs,
            endMs,
            isLocked,
            lockedStreamId,
            hasEligibleStreamer: hasEligible,
          });
        }
      }
      events.sort((a, b) => a.startMs - b.startMs);
      return events;
    };

    // ---- mbSelectDisplayEvents equivalent. Multitest2 line 5749. ----
    // Filter the relevant events down to those worth surfacing.
    const selectDisplayEvents = (events, now) => {
      const isStale = (e) => {
        if (e.isLocked) return false;
        const sinceShowtime = now - e.startMs;
        if (sinceShowtime < 0) return false;
        const phases = getSyncingPhases(e.showId);
        const hardCap = phases.initialMs + 2 * phases.extendedMs;
        if (sinceShowtime > hardCap) return true;
        return false;
      };
      const upNextOnly = events.filter(
        (e) => e.startMs > now + MB_COUNTDOWN_THRESHOLD_MS
      );
      const activeOrCountdown = events.filter(
        (e) =>
          e.startMs <= now + MB_COUNTDOWN_THRESHOLD_MS && !isStale(e)
      );
      const activePassing = activeOrCountdown.filter(
        (e) => e.isLocked || e.hasEligibleStreamer
      );
      if (activePassing.length > 0) return activePassing;
      const upNextEligible = upNextOnly.filter((e) => e.hasEligibleStreamer);
      if (upNextEligible.length > 0) {
        const earliest = upNextEligible[0].startMs;
        return upNextEligible.filter(
          (e) => Math.abs(e.startMs - earliest) < MB_CONSOLIDATE_WINDOW_MS
        );
      }
      return [];
    };

    // ---- Group events by same-start (within 60s). ----
    const consolidate = (eventList) => {
      const groups = [];
      for (const e of eventList) {
        let added = false;
        for (const g of groups) {
          if (Math.abs(e.startMs - g[0].startMs) < MB_CONSOLIDATE_WINDOW_MS) {
            g.push(e);
            added = true;
            break;
          }
        }
        if (!added) groups.push([e]);
      }
      return groups;
    };

    // ---- mbSyncingStartedAt tracking (per-show; lazily inits the
    // timestamp on first sight so the visible countdown is monotonic). ----
    const getSyncingRowData = (e, phaseNow) => {
      const startedAt = mbSyncingStartedAtRef.current;
      if (!startedAt[e.showId]) startedAt[e.showId] = phaseNow;
      const phases = getSyncingPhases(e.showId);
      const elapsed = phaseNow - startedAt[e.showId];
      let remaining, detail, phaseNum;
      if (elapsed < phases.initialMs) {
        remaining = phases.initialMs - elapsed;
        detail = "Syncing to show";
        phaseNum = 1;
      } else {
        const extElapsed = elapsed - phases.initialMs;
        const roundElapsed = extElapsed % phases.extendedMs;
        remaining = phases.extendedMs - roundElapsed;
        detail = "Taking a moment longer...";
        phaseNum = 2;
      }
      return { showId: e.showId, remaining, detail, phaseNum };
    };

    // ============================================================
    // Main compute
    // ============================================================
    const computeMbState = () => {
      const hidden = {
        topState: "hidden",
        lockedRows: [],
        nonLockedRows: [],
      };
      if (!beaconUserEnabled) return hidden;
      if (syncMode.active) return hidden;

      const now = Date.now();
      const phaseNow = now - MB_COUNTDOWN_DISPLAY_OFFSET_MS;
      const lw = listenerWindow;

      // hasFutureSchedule — any enabled show whose end is ahead of now.
      let hasFutureSchedule = false;
      for (const [showId, times] of Object.entries(showSchedule || {})) {
        if (!isShowEnabled(showId)) continue;
        if (!Array.isArray(times)) continue;
        const durationSec = MB_SHOW_DURATION_SEC[showId] || 1800;
        for (const dt of times) {
          if (dt.getTime() + durationSec * 1000 > now) {
            hasFutureSchedule = true;
            break;
          }
        }
        if (hasFutureSchedule) break;
      }

      // ---- CLOSED branch (lw enabled but not active). ----
      if (lw && lw.enabled && !lw.isActive) {
        if (hasFutureSchedule) {
          return {
            topState: "standby",
            lockedRows: [],
            nonLockedRows: [
              { type: "standby", detail: "Tap Here 5 Min Before Showtime" },
            ],
          };
        }
        const opensAt = formatListenerWindowStart();
        if (opensAt) {
          return {
            topState: "closed",
            lockedRows: [],
            nonLockedRows: [{ type: "closed", opensAt }],
          };
        }
        return hidden;
      }

      // ---- Compute events and bucket by phase. ----
      const relevantEvents = computeRelevantEvents(now);
      const events = selectDisplayEvents(relevantEvents, now);

      // Prune syncing tracker for shows no longer in events.
      const showingShowIds = new Set(events.map((e) => e.showId));
      for (const sid of Object.keys(mbSyncingStartedAtRef.current)) {
        if (!showingShowIds.has(sid)) delete mbSyncingStartedAtRef.current[sid];
      }

      const upcomingEvents = events.filter(
        (e) => !e.isLocked && e.startMs - phaseNow > MB_COUNTDOWN_THRESHOLD_MS
      );
      const countdownEvents = events.filter(
        (e) =>
          !e.isLocked &&
          e.startMs - phaseNow > 0 &&
          e.startMs - phaseNow <= MB_COUNTDOWN_THRESHOLD_MS
      );
      const syncingEvents = events.filter((e) => {
        if (e.isLocked) return false;
        const sinceShowtime = phaseNow - e.startMs;
        if (sinceShowtime < 0) return false;
        if (now > e.endMs) return false;
        const phases = getSyncingPhases(e.showId);
        const hardCap = phases.initialMs + 2 * phases.extendedMs;
        if (sinceShowtime > hardCap) return false;
        return true;
      });
      const lockedEvents = events.filter((e) => e.isLocked);

      const lockedRows = [];
      const nonLockedRows = [];

      // ---- Locked rows (primary). Consolidate same-start. ----
      const lockedGroups = consolidate(lockedEvents);
      for (const grp of lockedGroups) {
        const names = joinShowNames(grp.map((e) => prettyShow(e.showId)));
        const showIds = grp.map((e) => e.showId);
        lockedRows.push({
          type: "locked-cta",
          showIds,
          names,
          // The streamId from the FIRST event in the group — used by the
          // click handler to find a slot to enter sync from. When there
          // are multiple locks in the group, the picker (4h) will be
          // needed to disambiguate; for 5a-iii we route to the first.
          streamId: grp[0].lockedStreamId,
        });
      }

      // ---- Syncing rows (passive). Consolidate same-start. ----
      const syncingGroups = consolidate(syncingEvents);
      for (const grp of syncingGroups) {
        const datas = grp.map((e) => getSyncingRowData(e, phaseNow));
        if (datas.length === 1) {
          const d = datas[0];
          nonLockedRows.push({
            type: "syncing",
            names: prettyShow(d.showId),
            detail: d.detail,
            remaining: d.remaining,
          });
        } else {
          const names = joinShowNames(
            datas.map((d) => prettyShow(d.showId))
          );
          const maxRemaining = Math.max(...datas.map((d) => d.remaining));
          const anyPhase2 = datas.some((d) => d.phaseNum === 2);
          nonLockedRows.push({
            type: "syncing",
            names,
            detail: anyPhase2 ? "Taking a moment longer..." : "Syncing to shows",
            remaining: maxRemaining,
          });
        }
      }

      // ---- Countdown rows. Eligible → CTA, non-eligible → passive. ----
      const eligibleCd = countdownEvents.filter((e) => e.hasEligibleStreamer);
      const nonEligibleCd = countdownEvents.filter(
        (e) => !e.hasEligibleStreamer
      );
      if (eligibleCd.length >= 2) {
        const span =
          Math.max(...eligibleCd.map((e) => e.startMs)) -
          Math.min(...eligibleCd.map((e) => e.startMs));
        if (span < MB_CONSOLIDATE_WINDOW_MS) {
          // Single consolidated CTA
          const names = joinShowNames(
            eligibleCd.map((e) => prettyShow(e.showId))
          );
          nonLockedRows.push({
            type: "countdown-cta",
            showIds: eligibleCd.map((e) => e.showId),
            names,
          });
        } else {
          for (const e of eligibleCd) {
            nonLockedRows.push({
              type: "countdown-cta",
              showIds: [e.showId],
              names: prettyShow(e.showId),
            });
          }
        }
      } else if (eligibleCd.length === 1) {
        const e = eligibleCd[0];
        nonLockedRows.push({
          type: "countdown-cta",
          showIds: [e.showId],
          names: prettyShow(e.showId),
        });
      }
      for (const e of nonEligibleCd) {
        const msUntilStart = e.startMs - phaseNow;
        nonLockedRows.push({
          type: "countdown-preview",
          showName: prettyShow(e.showId),
          msUntilStart,
          urgent: msUntilStart <= MB_COUNTDOWN_URGENT_THRESHOLD_MS,
        });
      }

      // ---- Upcoming row (Up Next). Multitest2 line 6253. ----
      if (upcomingEvents.length > 0) {
        if (nonLockedRows.length === 0 && lockedRows.length === 0) {
          // Would be upcoming-only → swap for standby preview.
          nonLockedRows.push({
            type: "standby",
            detail: "Tap Here 5 Min Before Showtime",
          });
        } else {
          // Has other rows already → add Up Next as supplemental context.
          const earliest = upcomingEvents[0];
          const sameTime = upcomingEvents.filter(
            (e) => Math.abs(e.startMs - earliest.startMs) < MB_CONSOLIDATE_WINDOW_MS
          );
          const names = joinShowNames(
            sameTime.map((e) => prettyShow(e.showId))
          );
          nonLockedRows.push({
            type: "upcoming",
            names,
            formattedTime: formatShowTime(
              new Date(earliest.startMs),
              earliest.showId
            ),
          });
        }
      }

      // ---- Standby fallback when nothing else. ----
      if (lockedRows.length === 0 && nonLockedRows.length === 0) {
        if (lw && lw.isActive) {
          // "Searching for streamers..." only if pre-first-show AND a
          // show is in countdown without eligible streams.
          const eligibleSet = new Set();
          for (const stream of Object.values(eligibleStreams || {})) {
            const list = stream?.eligibleForShows;
            if (Array.isArray(list)) for (const s of list) eligibleSet.add(s);
          }
          let inCountdownWithoutEligible = false;
          if (!isPastFirstShowOfWindow()) {
            for (const [showId, dates] of Object.entries(showSchedule || {})) {
              if (!isShowEnabled(showId)) continue;
              if (!Array.isArray(dates)) continue;
              for (const d of dates) {
                const startMs = d.getTime();
                const untilStart = startMs - now;
                if (
                  untilStart > 0 &&
                  untilStart <= MB_COUNTDOWN_THRESHOLD_MS &&
                  !eligibleSet.has(showId)
                ) {
                  inCountdownWithoutEligible = true;
                  break;
                }
              }
              if (inCountdownWithoutEligible) break;
            }
          }
          if (inCountdownWithoutEligible) {
            nonLockedRows.push({
              type: "standby",
              detail: "Searching for streamers...",
            });
          } else if (hasFutureSchedule) {
            nonLockedRows.push({
              type: "standby",
              detail: "Tap Here 5 Min Before Showtime",
            });
          } else {
            nonLockedRows.push({
              type: "standby",
              detail: "Looking for streamers...",
            });
          }
        } else if (hasFutureSchedule) {
          // No lw response yet but a future show is on schedule.
          nonLockedRows.push({
            type: "standby",
            detail: "Tap Here 5 Min Before Showtime",
          });
        } else {
          return hidden;
        }
      }

      // ---- topState (priority: locked > syncing > countdown > upcoming
      //                > standby > closed) for the badge CSS class. ----
      let topState = "standby";
      if (lockedRows.length > 0) topState = "locked";
      else if (syncingEvents.length > 0) topState = "syncing";
      else if (countdownEvents.length > 0) topState = "countdown";
      else if (nonLockedRows.some((r) => r.type === "upcoming"))
        topState = "upcoming";
      else if (nonLockedRows.some((r) => r.type === "closed"))
        topState = "closed";

      return { topState, lockedRows, nonLockedRows };
    };

    const sameRows = (a, b) => {
      if (a.length !== b.length) return false;
      for (let i = 0; i < a.length; i++) {
        const ra = a[i];
        const rb = b[i];
        if (ra.type !== rb.type) return false;
        if (ra.names !== rb.names) return false;
        if (ra.detail !== rb.detail) return false;
        if (ra.opensAt !== rb.opensAt) return false;
        if (ra.remaining !== rb.remaining) return false;
        if (ra.formattedTime !== rb.formattedTime) return false;
        if (ra.urgent !== rb.urgent) return false;
        if (ra.showName !== rb.showName) return false;
        if (ra.msUntilStart !== rb.msUntilStart) return false;
        if ((ra.showIds || []).join(",") !== (rb.showIds || []).join(","))
          return false;
        if (ra.streamId !== rb.streamId) return false;
      }
      return true;
    };

    const tick = () => {
      setMbStatus((prev) => {
        const next = computeMbState();
        if (
          prev.topState === next.topState &&
          sameRows(prev.lockedRows || [], next.lockedRows) &&
          sameRows(prev.nonLockedRows || [], next.nonLockedRows)
        ) {
          return prev;
        }
        return next;
      });
    };

    tick();
    const id = setInterval(tick, MB_TICK_MS);
    return () => clearInterval(id);
  }, [
    beaconUserEnabled,
    syncMode.active,
    globalAnchors,
    fakeLocks,
    showSchedule,
    listenerWindow,
    monitorEnabled,
    eligibleStreams,
  ]);
  // ===== 5a-iv: mb-info-strip tick =====
  // 1Hz tick driving the slim band between the embed grid and thumbnail
  // scroll. Independent from the badge state machine; schedule-driven.
  // Mirrors multitest2 mbInfoStripTick line 6327.
  //
  // Priority (locked > candidates > "wrap"):
  //   - beacon off → hidden
  //   - any anchor present → "Now Playing: <names>"
  //   - candidates collection: schedule entries minus disabled/past/stale
  //   - "first show of window" rule: pre-first-show shows by schedule;
  //     post-first-show shows only when eligible streams exist
  //   - empty candidates AND lw enabled → "That's a wrap"
  //   - pre-showtime: "Up Next: <name> @ TIME"  or  "Up Next: <name> — Starts in M:SS"
  //   - post-showtime, no lock: "Starting Now: <name>"
  useEffect(() => {
    const computeInfoStrip = () => {
      if (!beaconUserEnabled) {
        return { visible: false, prefix: "", show: "", timeStr: null, countdownStr: null };
      }
      const now = Date.now();
      const phaseNow = now - MB_COUNTDOWN_DISPLAY_OFFSET_MS;
      const isShowEnabled = (showId) => monitorEnabled[showId] !== false;
      const eligibleForShow = (showId) => {
        let count = 0;
        for (const info of Object.values(eligibleStreams || {})) {
          const list = info?.eligibleForShows;
          if (Array.isArray(list) && list.includes(showId)) count++;
        }
        return count;
      };

      // 1. Locked shows take precedence.
      const allAnchors = { ...globalAnchors, ...fakeLocks };
      const lockedShowIds = [];
      const seenShowIds = new Set();
      for (const a of Object.values(allAnchors)) {
        if (a && a.showId && !seenShowIds.has(a.showId)) {
          seenShowIds.add(a.showId);
          lockedShowIds.push(a.showId);
        }
      }
      if (lockedShowIds.length > 0) {
        const names = lockedShowIds.map(prettyShow);
        return {
          visible: true,
          prefix: "Now Playing:",
          show: joinShowNames(names),
          timeStr: null,
          countdownStr: null,
        };
      }

      // 2. Build candidates from schedule.
      const candidates = [];
      for (const [showId, dates] of Object.entries(showSchedule || {})) {
        if (!isShowEnabled(showId)) continue;
        if (!Array.isArray(dates)) continue;
        for (const d of dates) {
          const startMs = d.getTime();
          const durationSec = MB_SHOW_DURATION_SEC[showId] || 1800;
          const endMs = startMs + durationSec * 1000;
          if (now > endMs + MB_POSTSHOW_HIDE_GRACE_MS) continue;
          const sinceShowtime = now - startMs;
          if (sinceShowtime >= 0) {
            const phases = getSyncingPhases(showId);
            const hardCap = phases.initialMs + 2 * phases.extendedMs;
            if (sinceShowtime > hardCap) continue;
          }
          candidates.push({ showId, startMs, date: d });
        }
      }

      const lwEnabled = listenerWindow && listenerWindow.enabled;

      if (candidates.length === 0) {
        if (lwEnabled) {
          return {
            visible: true,
            prefix: "That's a wrap!",
            show: "See you tomorrow",
            timeStr: null,
            countdownStr: null,
          };
        }
        return { visible: false, prefix: "", show: "", timeStr: null, countdownStr: null };
      }

      // 3. First-show-of-window rule.
      candidates.sort((a, b) => a.startMs - b.startMs);
      const winRange = computeListenerWindowRange(listenerWindow);
      const winStart = winRange ? winRange.startMs : 0;
      const winEnd = winRange ? winRange.endMs : Number.POSITIVE_INFINITY;
      let firstShowOfWindow = null;
      for (const [showId, dates] of Object.entries(showSchedule || {})) {
        if (!isShowEnabled(showId)) continue;
        if (!Array.isArray(dates)) continue;
        for (const d of dates) {
          const startMs = d.getTime();
          if (startMs < winStart || startMs > winEnd) continue;
          if (!firstShowOfWindow || startMs < firstShowOfWindow.startMs) {
            firstShowOfWindow = { showId, startMs };
          }
        }
      }
      const beforeFirstShow =
        firstShowOfWindow && now < firstShowOfWindow.startMs;
      let chosenCandidates;
      if (beforeFirstShow) {
        // Pre-first-show: surface first show by schedule + same-start co-shows.
        chosenCandidates = candidates.filter(
          (c) => Math.abs(c.startMs - firstShowOfWindow.startMs) < 60_000
        );
      } else {
        // Post-first-show: eligibility-only.
        chosenCandidates = candidates.filter(
          (c) => eligibleForShow(c.showId) > 0
        );
      }

      if (chosenCandidates.length === 0) {
        // Past first show with nothing eligible → consistent wrap message.
        if (lwEnabled) {
          return {
            visible: true,
            prefix: "That's a wrap!",
            show: "See you tomorrow",
            timeStr: null,
            countdownStr: null,
          };
        }
        return { visible: false, prefix: "", show: "", timeStr: null, countdownStr: null };
      }

      // 4. Pick soonest candidate + consolidate same-start co-shows.
      const soonestStartMs = chosenCandidates[0].startMs;
      const sameStartGroup = chosenCandidates.filter(
        (c) => Math.abs(c.startMs - soonestStartMs) < 60_000
      );
      const names = sameStartGroup.map((c) => prettyShow(c.showId));
      const nameStr = joinShowNames(names);
      const timeStr = formatShowTime(
        sameStartGroup[0].date,
        sameStartGroup[0].showId
      );
      const sinceShowtime = phaseNow - soonestStartMs;
      const timeToStartMs = -sinceShowtime;

      if (sinceShowtime < 0) {
        // Pre-showtime.
        if (
          timeToStartMs > 0 &&
          timeToStartMs <= MB_COUNTDOWN_THRESHOLD_MS
        ) {
          // Inside 5 min — countdown ONLY if at least one streamer is
          // eligible for any same-start show.
          let anyEligible = false;
          for (const c of sameStartGroup) {
            if (eligibleForShow(c.showId) > 0) {
              anyEligible = true;
              break;
            }
          }
          if (anyEligible) {
            return {
              visible: true,
              prefix: "Up Next:",
              show: nameStr,
              timeStr: null,
              countdownStr: formatCountdown(timeToStartMs),
            };
          }
        }
        return {
          visible: true,
          prefix: "Up Next:",
          show: nameStr,
          timeStr,
          countdownStr: null,
        };
      }
      // Post-showtime, no lock.
      return {
        visible: true,
        prefix: "Starting Now:",
        show: nameStr,
        timeStr: null,
        countdownStr: null,
      };
    };

    const tick = () => {
      const next = computeInfoStrip();
      setInfoStrip((prev) => {
        // Skip setState if shape unchanged (avoid pointless re-renders).
        if (
          prev.visible === next.visible &&
          prev.prefix === next.prefix &&
          prev.show === next.show &&
          prev.timeStr === next.timeStr &&
          prev.countdownStr === next.countdownStr
        ) {
          return prev;
        }
        return next;
      });
    };

    tick();
    const id = setInterval(tick, MB_TICK_MS);
    return () => clearInterval(id);
  }, [
    beaconUserEnabled,
    globalAnchors,
    fakeLocks,
    showSchedule,
    listenerWindow,
    monitorEnabled,
    eligibleStreams,
  ]);
  // ===== 5f: embed-fullscreen in-slot badge tick =====
  // 1Hz. For the currently fullscreen slot, resolve its badge phase via
  // getActiveBadgeShowForStream and build the row data + peer offer.
  // Mirrors multitest2 decorateSlot fs-mb-status block (line 2955) +
  // the per-phase copy (2989-3075). Countdown phase auto-promotes into
  // HLS early-entry (the customer is "whisked into doors").
  useEffect(() => {
    const tick = () => {
      const next = {};
      // Only the fullscreen slot can show an in-slot badge.
      if (
        beaconUserEnabled &&
        !syncMode.active &&
        fullscreenIndex != null
      ) {
        const idx = fullscreenIndex;
        const vid = videoIds[idx];
        const sid = vid ? resolveStreamId(vid, videoIdToStreamer[vid]) : null;
        const ctx = sid ? getActiveBadgeShowForStream(sid) : null;
        // === BADGE DIAGNOSTIC — REMOVE ONCE MISSING-SYNC-BADGE BUG SOLVED ====
        // Log once per second when fullscreened but state is missing or
        // incomplete, so when the bug repros we can see what was going on.
        if (!ctx || (!["preview", "countdown", "syncing", "locked"].includes(
              ctx && ctx.phase
            ))) {
          const k = `${idx}:${sid}:${ctx ? ctx.phase : "no-ctx"}`;
          if (badgeDiagLastKeyRef.current !== k) {
            badgeDiagLastKeyRef.current = k;
            console.warn(
              "[beacon DIAG] fullscreen but no badge:",
              {
                idx,
                videoId: vid,
                streamId: sid,
                streamerName: videoIdToStreamer[vid],
                ctx,
                beaconUserEnabled,
                syncActive: syncMode.active,
              }
            );
          }
        } else {
          badgeDiagLastKeyRef.current = null;
        }
        // ======================================================================
        if (ctx) {
          const showName = prettyShow(ctx.showId);
          const showTimeStr = ctx.startMs
            ? formatShowTime(new Date(ctx.startMs), ctx.showId)
            : "";
          let entry = null;
          if (ctx.phase === "preview" || ctx.phase === "countdown") {
            entry = {
              phase: ctx.phase,
              showId: ctx.showId,
              showName,
              showTimeStr,
              label: "MAGIC BEACON AUTOSYNC",
              detail: "Your bands will automatically pair to this stream!",
              tappable: false,
              peerId: null,
            };
            // Countdown → auto-promote into HLS early-entry (deferred a
            // tick so we don't recurse inside the render path).
            //
            // GUARD: we add BOTH the countdown key AND the locked key so
            // that if the lock arrives mid-HLS and the user exits, the
            // locked-phase auto-promote sees this (slot, show) as already
            // promoted and does NOT re-launch HLS. Without the symmetric
            // guard, the user is trapped in an exit→re-enter loop:
            //   countdown auto-promote sets "idx:showId" →
            //   lock arrives in HLS → user exits →
            //   ctx.phase becomes "locked" →
            //   locked branch checks "idx:showId:locked" — not found →
            //   re-fires auto-promote.
            if (ctx.phase === "countdown") {
              const guardKey = `${idx}:${ctx.showId}`;
              if (!autoPromoteFiredRef.current.has(guardKey)) {
                autoPromoteFiredRef.current.add(guardKey);
                // Pre-add the locked-phase guard so a later lock arrival
                // can't re-auto-promote the same (slot, show) after user
                // exits HLS. See block comment above autoPromoteFiredRef.
                autoPromoteFiredRef.current.add(`${guardKey}:locked`);
                setTimeout(() => {
                  if (syncMode.active) return;
                  if (fullscreenIndex !== idx) return;
                  const recheck = sid
                    ? getActiveBadgeShowForStream(sid)
                    : null;
                  if (!recheck || recheck.phase !== "countdown") return;
                  console.log(
                    `[beacon] slot ${idx}: fullscreen + countdown (${recheck.showId}) — auto-promoting to HLS`
                  );
                  enterSyncModeInternal(idx, {
                    earlyEntry: true,
                    showId: recheck.showId,
                    showtimeMs: recheck.startMs || null,
                  });
                }, 0);
              }
            }
          } else if (ctx.phase === "syncing") {
            // Seed/track the per-show syncing start for the countdown.
            const started = mbSyncingStartedAtRef.current;
            if (!started[ctx.showId]) started[ctx.showId] = Date.now();
            const phases = getSyncingPhases(ctx.showId);
            const elapsed = Date.now() - started[ctx.showId];
            let detail, remaining;
            if (elapsed < phases.initialMs) {
              detail = "Syncing to show";
              remaining = phases.initialMs - elapsed;
            } else {
              const ext = (elapsed - phases.initialMs) % phases.extendedMs;
              detail = "Taking a moment longer...";
              remaining = phases.extendedMs - ext;
            }
            // Peer offer: another streamer locked on the same show?
            const peer = findLockedPeer(ctx.showId, sid);
            entry = {
              phase: "syncing",
              showId: ctx.showId,
              showName,
              showTimeStr: "",
              label: "MAGIC BEACON",
              detail,
              remaining,
              tappable: false,
              peerId: peer ? peer.streamId : null,
            };
          } else if (ctx.phase === "locked") {
            entry = {
              phase: "locked",
              showId: ctx.showId,
              showName,
              showTimeStr: "",
              label: "SYNC YOUR BAND!",
              detail: "✨ Connected! TAP HERE TO SYNC NOW!",
              tappable: true,
              peerId: null,
            };
            // Auto-promote into HLS when fullscreened on a locked stream.
            // Without this, the user is shown a green "TAP HERE" badge
            // they can't easily activate from a remote.
            //
            // GUARD: same symmetric pattern as countdown — pre-add both
            // the locked key AND the countdown key so that any future
            // phase change for this (slot, show) does NOT re-trigger
            // auto-promote. See block comment above autoPromoteFiredRef.
            const lockedGuardKey = `${idx}:${ctx.showId}:locked`;
            if (!autoPromoteFiredRef.current.has(lockedGuardKey)) {
              autoPromoteFiredRef.current.add(lockedGuardKey);
              // Pre-add the countdown-phase guard for the symmetric case
              // (rare in practice — locked phase usually doesn't regress
              // to countdown — but covers any future phase reshuffle).
              autoPromoteFiredRef.current.add(`${idx}:${ctx.showId}`);
              setTimeout(() => {
                if (syncMode.active) return;
                if (fullscreenIndex !== idx) return;
                const recheck = sid
                  ? getActiveBadgeShowForStream(sid)
                  : null;
                if (!recheck || recheck.phase !== "locked") return;
                console.log(
                  `[beacon] slot ${idx}: fullscreen + locked (${recheck.showId}) — auto-promoting to HLS`
                );
                enterSyncMode(idx);
              }, 0);
            }
          }
          if (entry) next[idx] = entry;
        }
      }

      // Clear stale autoPromoteFiredRef entries. Any pair that's no
      // longer in its auto-promote phase in `next` should be removed
      // so a future legitimate phase can auto-promote. Skipped while
      // syncMode is active: fsBadges is empty during sync (gated above),
      // so clearing here would lose the guard and let exitSyncMode
      // immediately re-fire the auto-promote on the same phase.
      //
      // Key formats:
      //   "<idx>:<showId>"          — countdown phase guard
      //   "<idx>:<showId>:locked"   — locked phase guard
      //
      // Note: With the symmetric-guard fix above (both keys added on
      // every auto-promote), one key may be present while the current
      // phase matches the other. That's correct — we WANT to keep both
      // guards alive so neither phase can re-promote. The cleanup keeps
      // a key as long as its corresponding phase matches OR the OTHER
      // phase key still exists for the same (slot, show).
      if (!syncMode.active && autoPromoteFiredRef.current.size) {
        const keep = new Set();
        // First pass: keep keys whose phase matches current next[].
        for (const key of autoPromoteFiredRef.current) {
          const parts = key.split(":");
          const slotIdx = Number(parts[0]);
          const showId = parts[1];
          const kind = parts[2] || "countdown";
          const cur = next[slotIdx];
          if (cur && cur.phase === kind && cur.showId === showId) {
            keep.add(key);
          }
        }
        // Second pass: for each kept key, also keep its symmetric sibling
        // so the next phase change can't re-promote. E.g. if "0:hea" is
        // kept (countdown matches), also keep "0:hea:locked" so a lock
        // arrival doesn't re-launch HLS.
        const siblings = new Set();
        for (const key of keep) {
          if (key.endsWith(":locked")) {
            siblings.add(key.slice(0, -":locked".length));
          } else {
            siblings.add(`${key}:locked`);
          }
        }
        // Only keep the sibling if it was previously in the set (don't
        // synthesize new guards — only preserve existing ones).
        for (const sib of siblings) {
          if (autoPromoteFiredRef.current.has(sib)) keep.add(sib);
        }
        autoPromoteFiredRef.current = keep;
      }

      setFsBadges((prev) => {
        // Shallow-compare to avoid pointless re-renders.
        const pk = Object.keys(prev);
        const nk = Object.keys(next);
        if (pk.length === nk.length) {
          let same = true;
          for (const k of nk) {
            const a = prev[k];
            const b = next[k];
            if (
              !a ||
              a.phase !== b.phase ||
              a.showId !== b.showId ||
              a.detail !== b.detail ||
              a.remaining !== b.remaining ||
              a.tappable !== b.tappable ||
              a.peerId !== b.peerId ||
              a.showTimeStr !== b.showTimeStr ||
              a.label !== b.label
            ) {
              same = false;
              break;
            }
          }
          if (same) return prev;
        }
        return next;
      });
    };

    tick();
    const id = setInterval(tick, MB_TICK_MS);
    return () => clearInterval(id);
  }, [
    beaconUserEnabled,
    syncMode.active,
    fullscreenIndex,
    videoIds,
    globalAnchors,
    fakeLocks,
    showSchedule,
    monitorEnabled,
    eligibleStreams,
  ]);
  // ===== 4f: pre-show wake scheduler =====
  // 1Hz tick. Fires BLE wake commands at -60s and -30s before showtime
  // so the band lights up in advance. Multitest2 line 4618.
  //
  // Window 1: 60s ≥ untilShow > 30s  → one-shot WAKE_UP_SLOW
  // Window 2: 30s ≥ untilShow ≥ 20s  → WAKE_UP_SLOW now, again at +8s,
  //                                     then WAKE_UP_FAST at +16s
  //
  // Gates (in order):
  //   - beacon master toggle off → skip entirely
  //   - doors active (body.mb-doors-active set by SyncFullscreen) →
  //     skip; doors run their own wake sequence
  //   - no schedule yet OR no eligible streams yet → skip
  //   - show not enabled (dashboard toggle off) → skip
  //   - in HLS sync for a DIFFERENT show → skip (wakes for other shows
  //     would interfere with committed band session)
  useEffect(() => {
    const wakeKey = (showId, startMs) => `${showId}:${startMs}`;
    const isShowEnabled = (showId) => monitorEnabled[showId] !== false;
    const isDoorsActive = () =>
      document.body.classList.contains("mb-doors-active");

    const pruneWakeState = () => {
      const now = Date.now();
      const live = new Set();
      for (const [showId, dates] of Object.entries(showSchedule || {})) {
        if (!isShowEnabled(showId)) continue;
        if (!Array.isArray(dates)) continue;
        const durationSec = MB_SHOW_DURATION_SEC[showId] || 1800;
        for (const d of dates) {
          const startMs = d.getTime();
          const endMs = startMs + durationSec * 1000;
          if (now <= endMs + MB_POSTSHOW_HIDE_GRACE_MS) {
            live.add(wakeKey(showId, startMs));
          }
        }
      }
      for (const k of Object.keys(wakeSent60Ref.current)) {
        if (!live.has(k)) delete wakeSent60Ref.current[k];
      }
      for (const k of Object.keys(wakeSent30Ref.current)) {
        if (!live.has(k)) delete wakeSent30Ref.current[k];
      }
    };

    const tick = () => {
      if (!beaconUserEnabled) return;
      if (isDoorsActive()) return;
      if (!showSchedule || Object.keys(showSchedule).length === 0) return;
      if (!eligibleStreams || Object.keys(eligibleStreams).length === 0)
        return;

      // Which shows have AT LEAST ONE currently-eligible stream?
      const eligibleShowSet = new Set();
      for (const stream of Object.values(eligibleStreams)) {
        if (Array.isArray(stream?.eligibleForShows)) {
          for (const s of stream.eligibleForShows) eligibleShowSet.add(s);
        }
      }
      if (eligibleShowSet.size === 0) return;

      const committedShowId = syncMode.active
        ? syncMode.earlyEntryShowId ||
          (syncMode.anchor && syncMode.anchor.showId)
        : null;

      const now = Date.now();
      // Same display offset the widget uses, so the visible countdown
      // and the wake-command timing line up exactly.
      const phaseNow = now - MB_COUNTDOWN_DISPLAY_OFFSET_MS;
      const deviceId = beaconDeviceIdRef.current;

      for (const [showId, dates] of Object.entries(showSchedule)) {
        if (!isShowEnabled(showId)) continue;
        if (committedShowId && showId !== committedShowId) continue;
        if (!eligibleShowSet.has(showId)) continue;
        if (!Array.isArray(dates)) continue;

        for (const d of dates) {
          const startMs = d.getTime();
          const untilShow = startMs - phaseNow;
          const key = wakeKey(showId, startMs);

          // Window 1: -60s to -30s — single WAKE_UP_SLOW.
          if (
            untilShow <= 60_000 &&
            untilShow > 30_000 &&
            !wakeSent60Ref.current[key]
          ) {
            wakeSent60Ref.current[key] = true;
            console.log(
              `[beacon] preshow wake: ${showId} −60s → WAKE_UP_SLOW`
            );
            gatedSendBeaconCmd(deviceId, "WAKE_UP_SLOW", "OFF");
          }

          // Window 2: -30s to -20s — 3-burst.
          if (
            untilShow <= 30_000 &&
            untilShow >= 20_000 &&
            !wakeSent30Ref.current[key]
          ) {
            wakeSent30Ref.current[key] = true;
            console.log(
              `[beacon] preshow wake: ${showId} −30s → WAKE_UP_SLOW × 2, then WAKE_UP_FAST`
            );
            gatedSendBeaconCmd(deviceId, "WAKE_UP_SLOW", "OFF");

            const id1 = setTimeout(() => {
              if (!beaconUserEnabled) return;
              if (isDoorsActive()) return;
              // Re-check committed show — customer may have entered
              // sync between when we scheduled this and when it fires.
              if (syncMode.active) {
                const csid =
                  syncMode.earlyEntryShowId ||
                  (syncMode.anchor && syncMode.anchor.showId);
                if (csid && csid !== showId) return;
              }
              gatedSendBeaconCmd(
                beaconDeviceIdRef.current,
                "WAKE_UP_SLOW",
                "OFF"
              );
            }, 8000);
            pendingWakeTimeoutsRef.current.push(id1);

            const id2 = setTimeout(() => {
              if (!beaconUserEnabled) return;
              if (isDoorsActive()) return;
              if (syncMode.active) {
                const csid =
                  syncMode.earlyEntryShowId ||
                  (syncMode.anchor && syncMode.anchor.showId);
                if (csid && csid !== showId) return;
              }
              gatedSendBeaconCmd(
                beaconDeviceIdRef.current,
                "WAKE_UP_FAST",
                "OFF"
              );
            }, 16000);
            pendingWakeTimeoutsRef.current.push(id2);
          }
        }
      }

      pruneWakeState();
    };

    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [
    beaconUserEnabled,
    syncMode.active,
    syncMode.anchor,
    syncMode.earlyEntryShowId,
    showSchedule,
    monitorEnabled,
    eligibleStreams,
  ]);

  // ===== 4f: cancel pending wakes when sync enters =====
  // Doors take over the wake duty authoritatively at sync entry —
  // multiview-side pending wakes would hit the band twice. Mirrors
  // multitest2 line 2374 (mbDoorsStart calls mbCancelPendingPreshowWakes).
  useEffect(() => {
    if (syncMode.active && pendingWakeTimeoutsRef.current.length > 0) {
      let cleared = 0;
      while (pendingWakeTimeoutsRef.current.length) {
        const id = pendingWakeTimeoutsRef.current.pop();
        if (id) {
          clearTimeout(id);
          cleared++;
        }
      }
      console.log(
        `[beacon] preshow wake: ${cleared} pending timeout(s) canceled (sync entering)`
      );
    }
  }, [syncMode.active]);

  // ===== 4f: cancel pending wakes when beacon is toggled off =====
  useEffect(() => {
    if (!beaconUserEnabled && pendingWakeTimeoutsRef.current.length > 0) {
      while (pendingWakeTimeoutsRef.current.length) {
        const id = pendingWakeTimeoutsRef.current.pop();
        if (id) clearTimeout(id);
      }
      console.log(
        "[beacon] preshow wake: pending timeouts canceled (beacon disabled)"
      );
    }
  }, [beaconUserEnabled]);
  // ===== 4h: picker maintenance =====
  // Runs at 1Hz while the picker is open. Two jobs (mirrors multitest2
  // mbRender lines 5853-5882):
  //   1. Auto-close-empty: if the picker's source set drops to zero
  //      (all locks cleared, no eligible streams left, etc.), close.
  //   2. Refresh contents: rebuild items array if the underlying set
  //      changed. React reconciles tile updates declaratively, so we
  //      just call setStreamPicker with a fresh items list.
  // Also auto-closes the picker when sync mode enters (the picker is a
  // multiview-side concept).
  useEffect(() => {
    if (!streamPicker.open) return;

    // Auto-close on sync entry.
    if (syncMode.active) {
      closeStreamPicker();
      return;
    }

    const tick = () => {
      let items;
      if (streamPicker.mode === "early" && streamPicker.showId) {
        items = getEligibleStreamsForShow(streamPicker.showId);
      } else if (
        streamPicker.mode === "early-multi" &&
        streamPicker.showIds
      ) {
        items = [];
        const seen = new Set();
        for (const sId of streamPicker.showIds) {
          for (const s of getEligibleStreamsForShow(sId)) {
            if (seen.has(s.streamId)) continue;
            seen.add(s.streamId);
            items.push({ ...s, pickerShowId: sId });
          }
        }
      } else if (
        streamPicker.mode === "locked-multi" &&
        streamPicker.showIds
      ) {
        items = [];
        const seen = new Set();
        const allLocked = getLockedStreams();
        for (const sId of streamPicker.showIds) {
          for (const ls of allLocked) {
            if (seen.has(ls.streamId)) continue;
            const anchor = getAnyAnchor(ls.streamId);
            if (!anchor || anchor.showId !== sId) continue;
            seen.add(ls.streamId);
            items.push({ ...ls, pickerShowId: sId });
          }
        }
      } else {
        items = getLockedStreams();
      }

      if (items.length === 0) {
        closeStreamPicker();
        return;
      }

      // Compare current items to last rendered. Only setState if changed.
      const prevIds = streamPicker.items
        .map((it) => it.streamId)
        .sort()
        .join(",");
      const nextIds = items.map((it) => it.streamId).sort().join(",");
      if (prevIds !== nextIds) {
        setStreamPicker((prev) => ({ ...prev, items }));
      }
    };

    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [
    streamPicker.open,
    streamPicker.mode,
    streamPicker.showId,
    streamPicker.showIds,
    streamPicker.items,
    syncMode.active,
    globalAnchors,
    fakeLocks,
    eligibleStreams,
    roster,
    videoIds,
  ]);
  // ===== Window dev helpers =====
  useEffect(() => {
    window.beaconFakeLock = (streamId, showId) => {
      if (!streamId) {
        console.warn(
          "usage: beaconFakeLock('UC...streamId', [showId])\n" +
            "  showId optional — pass a real show key to lock on that show " +
            "(strip shows it). NOTE: the top-right badge only goes GREEN when " +
            "the show is actually in the live schedule (i.e. at real showtime); " +
            "during the day it stays purple even with a fake lock. That's a " +
            "test-only limitation — real locks at showtime flip it green."
        );
        return;
      }
      const show = showId || "fake-test";
      // Pin the anchor to the show's REAL scheduled showtime (closest to
      // the simmed now) instead of `now − 5s`. Otherwise, as beaconSimNow's
      // fixed offset lets the clock keep ticking, the inferred showtime
      // (anchorPDTMs − anchorShowPosSec) drifts and the lock-match flickers
      // in and out → bar flips locked↔standby every second. Pinning to the
      // schedule entry keeps `startMs === bestLockShowtime` rock-stable.
      const now = Date.now();
      let pinnedShowtimeMs = null;
      const dates = showSchedule[show];
      if (Array.isArray(dates) && dates.length > 0) {
        let bestDelta = Infinity;
        for (const d of dates) {
          const ms = d.getTime();
          const delta = Math.abs(ms - now);
          if (delta < bestDelta) {
            bestDelta = delta;
            pinnedShowtimeMs = ms;
          }
        }
      }
      // anchorShowPosSec = a few seconds into the show; anchorPDTMs is then
      // pinnedShowtime + that offset, so inferredStart === pinnedShowtime.
      const anchorShowPosSec = 5;
      const anchorPDTMs =
        pinnedShowtimeMs != null
          ? pinnedShowtimeMs + anchorShowPosSec * 1000
          : now - 5000;
      setFakeLocks((prev) => ({
        ...prev,
        [streamId]: {
          showId: show,
          anchorPDTMs,
          anchorShowPosSec,
        },
      }));
      console.log(
        `🔮 fake lock added: ${streamId} on ${show}` +
          (pinnedShowtimeMs != null
            ? ` (pinned to scheduled ${new Date(
                pinnedShowtimeMs
              ).toLocaleTimeString()})`
            : showId
            ? " (badge goes green only if this show is in the live schedule)"
            : " (fake-test → strip flips, badge stays purple)")
      );
    };
    window.beaconFakeUnlock = (streamId) => {
      setFakeLocks((prev) => {
        if (!streamId) return {};
        const next = { ...prev };
        delete next[streamId];
        return next;
      });
      console.log("🔮 fake lock removed:", streamId || "(all)");
    };
    window.beaconFakeLockAll = () => {
      const all = {};
      const allStreamIds = new Set([
        ...Object.values(streamIdByVideoId),
        ...Object.values(streamIdByName),
      ]);
      for (const sid of allStreamIds) {
        all[sid] = {
          showId: "fake-test",
          anchorPDTMs: Date.now() - 5000,
          anchorShowPosSec: 5,
        };
      }
      setFakeLocks(all);
      console.log(
        "🔮 fake locked all streams in roster:",
        Object.keys(all).length
      );
    };
    window.beaconForceEarlyEntry = (slotIndex, showId, secondsUntilShowtime) => {
      if (typeof slotIndex !== "number" || !showId || typeof secondsUntilShowtime !== "number") {
        console.warn(
          "usage: beaconForceEarlyEntry(slotIndex, showId, secondsUntilShowtime)"
        );
        return;
      }
      enterSyncModeInternal(slotIndex, {
        earlyEntry: true,
        showId,
        showtimeMs: Date.now() + secondsUntilShowtime * 1000,
      });
    };
    window.beaconUserToggle = () => handleBeaconToggle();

    // 5d: enter an HEA HLS session directly for Tink testing. Fake-locks
    // the stream in `slotIndex` (default 0) on happily-ever-after, then
    // enters sync via the LOCKED path (with an anchor) so the radar arms
    // (TinkTracker gates on a post-lock HEA anchor). Once in, use
    // window.beaconTestTink(secondsBeforeFlyoff) to fire the flyoff.
    window.beaconTinkEnter = (slotIndex = 0) => {
      const vid = videoIds[slotIndex];
      if (!vid) {
        console.warn(`[beacon] beaconTinkEnter: no stream in slot ${slotIndex}`);
        return;
      }
      const sid = resolveStreamId(vid, videoIdToStreamer[vid]);
      if (!sid) {
        console.warn(`[beacon] beaconTinkEnter: no streamId for slot ${slotIndex}`);
        return;
      }
      // Fake-lock it on HEA with a showPos ~30s in, so the cue runner has
      // a meaningful anchor immediately and the radar arms on first tick.
      setFakeLocks((prev) => ({
        ...prev,
        [sid]: {
          showId: "happily-ever-after",
          anchorPDTMs: Date.now() - 30_000,
          anchorShowPosSec: 30,
        },
      }));
      console.log(
        `🧚 beaconTinkEnter: fake-locked ${sid} on HEA, entering HLS in 600ms…\n` +
          "   once the radar shows, run beaconTestTink(5) to fire the flyoff."
      );
      // Give the fakeLock state a beat to commit, then enter locked sync.
      setTimeout(() => enterSyncMode(slotIndex), 600);
    };

    // 5c: set the stream-health warning stage on the CURRENTLY SYNCED
    // stream's anchor (0 = clear, 1 = yellow, 2 = red). Writes into the
    // fake lock if present, else into globalAnchors, so the warning bar
    // appears in the sync screen. Mirrors multitest2 line 7286/7300.
    //
    // Optional 2nd arg peerStreamId: also fake-locks that peer on the SAME
    // show as the synced stream, so the warning becomes a tappable
    // "TAP to switch streamers" handoff. Without it (or no real peer
    // locked on the same show), the bar shows the no-peer body text.
    window.beaconTestWarning = (stage, peerStreamId) => {
      const sid = syncMode.streamId;
      if (!sid) {
        console.warn("[beacon] beaconTestWarning: not in sync mode");
        return;
      }
      const s = stage === 1 || stage === 2 ? stage : 0;
      // The show the synced stream is on (real anchor or fake lock).
      const myShow =
        (syncMode.anchor && syncMode.anchor.showId) ||
        (fakeLocksRef.current[sid] && fakeLocksRef.current[sid].showId) ||
        syncMode.earlyEntryShowId ||
        "fake-test";
      if (fakeLocksRef.current && fakeLocksRef.current[sid]) {
        setFakeLocks((prev) => {
          const next = {
            ...prev,
            [sid]: { ...prev[sid], streamHealthWarning: s },
          };
          if (peerStreamId && peerStreamId !== sid) {
            next[peerStreamId] = {
              showId: myShow,
              anchorPDTMs: Date.now() - 5000,
              anchorShowPosSec: 5,
            };
          }
          return next;
        });
      } else {
        setGlobalAnchors((prev) => ({
          ...prev,
          [sid]: { ...(prev[sid] || {}), streamHealthWarning: s },
        }));
        if (peerStreamId && peerStreamId !== sid) {
          setFakeLocks((prev) => ({
            ...prev,
            [peerStreamId]: {
              showId: myShow,
              anchorPDTMs: Date.now() - 5000,
              anchorShowPosSec: 5,
            },
          }));
        }
      }
      console.log(
        `🩺 stream-health warning stage ${s} on ${sid} (show ${myShow})` +
          (peerStreamId ? ` + peer ${peerStreamId} locked on same show` : "") +
          (s ? "" : " (cleared)")
      );
    };

    // 5b: dump every streamId the app knows (slot videoIds + roster) with
    // names + which slot they're in, for picking a peer to fake-lock.
    window.beaconStreamIds = () => {
      const out = [];
      videoIds.forEach((vid, i) => {
        if (!vid) return;
        const name = videoIdToStreamer[vid];
        const sid = resolveStreamId(vid, name);
        out.push({ slot: i, streamId: sid, name, videoId: vid });
      });
      roster.forEach((r) => {
        if (!r.streamId) return;
        if (out.some((o) => o.streamId === r.streamId)) return;
        out.push({ slot: null, streamId: r.streamId, name: r.name });
      });
      console.table(out);
      return out;
    };

    // 5b: one-shot peer-handoff test. Enters early-entry sync on `slotIndex`
    // for the fake-test show (showtime already past so it lands in the
    // syncing wait), then fake-locks `peerStreamId` on that same show so
    // the gold peer bubble pops. Pass a peerStreamId NOT in slotIndex.
    window.beaconTestPeer = (slotIndex, peerStreamId) => {
      if (typeof slotIndex !== "number" || !peerStreamId) {
        console.warn("usage: beaconTestPeer(slotIndex, 'peerStreamId')");
        return;
      }
      // showtime 2s in the past → immediately in the syncing wait
      enterSyncModeInternal(slotIndex, {
        earlyEntry: true,
        showId: "fake-test",
        showtimeMs: Date.now() - 2000,
      });
      // fake-lock the peer on the same show after sync mounts
      setTimeout(() => {
        setFakeLocks((prev) => ({
          ...prev,
          [peerStreamId]: {
            showId: "fake-test",
            anchorPDTMs: Date.now() - 5000,
            anchorShowPosSec: 5,
          },
        }));
        console.log("🔮 peer fake-locked on fake-test:", peerStreamId);
        console.log("→ gold bubble should pop in the sync screen now");
      }, 1500);
    };

    window.beaconState = () => ({
      beaconUserEnabled,
      tinkUserEnabled,
      hlsVolume,
      hlsMuted,
      syncActive: syncMode.active,
      gearExpanded,
    });
    // 5a-i: inspect current widget state from devtools.
    window.beaconMbStatus = () => ({ ...mbStatus });

    // 4f: inspect pre-show wake scheduler state.
    window.beaconWakeState = () => ({
      sent60: { ...wakeSent60Ref.current },
      sent30: { ...wakeSent30Ref.current },
      pendingTimeouts: pendingWakeTimeoutsRef.current.length,
    });

    // Debug: inspect the auto-promote guard set. Useful when chasing
    // user-exits-HLS-loop reproductions to see which keys are present.
    window.beaconAutoPromoteGuards = () =>
      Array.from(autoPromoteFiredRef.current);

    // 4h: open/close picker from console.
    //   beaconOpenPicker()                  → locked picker
    //   beaconOpenPicker("happily-ever-after") → early-entry picker
    //   beaconClosePicker()                 → close
    window.beaconOpenPicker = (showId) => {
      if (showId) openEarlyEntryPicker(showId);
      else openStreamPicker();
    };
    window.beaconClosePicker = () => closeStreamPicker();

    // 5a-iv: inspect info strip state.
    window.beaconInfoStrip = () => ({ ...infoStrip });

    // 5a-ii: simulate a different "now" so countdown / closed / standby
    // transitions can be tested without waiting for actual showtimes.
    // Monkey-patches Date.now globally; the state-machine tick picks it
    // up within 1s. Use beaconSimReset() to restore real time.
    //
    //   beaconSimNow("21:56")       → today at 9:56 PM ET
    //   beaconSimNow("21:56", "PT") → today at 9:56 PM PT
    //   beaconSimReset()            → real time
    if (!window.__beaconRealDateNow) {
      window.__beaconRealDateNow = Date.now.bind(Date);
    }
    window.beaconSimNow = (etTimeStr, tzLabel = "ET") => {
      const m = etTimeStr && etTimeStr.match(/^(\d{1,2}):(\d{2})$/);
      if (!m) {
        console.warn('usage: beaconSimNow("21:56") — 24-hour HH:MM');
        return;
      }
      const h = Number(m[1]);
      const min = Number(m[2]);
      const tzMap = {
        ET: "America/New_York",
        PT: "America/Los_Angeles",
        CT: "America/Chicago",
        MT: "America/Denver",
      };
      const tz = tzMap[tzLabel] || "America/New_York";
      // Today's wall-clock in target TZ.
      const realNowReal = window.__beaconRealDateNow();
      const realNowDate = new Date(realNowReal);
      const tzNow = new Date(
        realNowDate.toLocaleString("en-US", { timeZone: tz })
      );
      const target = new Date(tzNow);
      target.setHours(h, min, 0, 0);
      const tzOffsetMs = tzNow.getTime() - realNowDate.getTime();
      const targetLocalMs = target.getTime() - tzOffsetMs;
      const simOffset = targetLocalMs - realNowReal;
      Date.now = () => window.__beaconRealDateNow() + simOffset;
      const stamp = new Date(targetLocalMs).toLocaleString("en-US", {
        timeZone: tz,
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      });
      console.log(`🕒 [beacon sim] now → ${stamp} ${tzLabel}`);
    };
    window.beaconSimReset = () => {
      if (window.__beaconRealDateNow) {
        Date.now = window.__beaconRealDateNow;
      }
      // Also drop any forced listener window so we go back to the backend's.
      setListenerWindow((prev) =>
        prev && prev.__forced
          ? { ...prev, isActive: prev.__realIsActive, __forced: false }
          : prev
      );
      console.log("🕒 [beacon sim] reset — using real time + real window");
    };

    // Force the listener window active (or inactive) so a simmed clock can
    // actually reach the syncing/locked logic during the day. The backend
    // sets listenerWindow.isActive from REAL time, so without this the
    // CLOSED short-circuit (lw.enabled && !lw.isActive) intercepts every
    // simmed state and you only ever see standby. Pair with beaconSimNow.
    //   beaconSimWindow(true)  → force isActive true  (show hours)
    //   beaconSimWindow(false) → force isActive false (closed)
    //   beaconSimReset()       → restore backend value
    window.beaconSimWindow = (active = true) => {
      setListenerWindow((prev) => {
        const base = prev || { enabled: true, start: "07:00", end: "03:00" };
        return {
          ...base,
          enabled: true,
          isActive: !!active,
          __forced: true,
          __realIsActive:
            prev && prev.__forced ? prev.__realIsActive : base.isActive,
        };
      });
      console.log(
        `🪟 [beacon sim] listener window forced → isActive=${!!active}`
      );
    };

    return () => {
      delete window.beaconFakeLock;
      delete window.beaconFakeUnlock;
      delete window.beaconFakeLockAll;
      delete window.beaconStreamIds;
      delete window.beaconTestPeer;
      delete window.beaconTestWarning;
      delete window.beaconForceEarlyEntry;
      delete window.beaconTinkEnter;
      delete window.beaconUserToggle;
      delete window.beaconState;
      delete window.beaconMbStatus;
      delete window.beaconWakeState;
      delete window.beaconAutoPromoteGuards;
      delete window.beaconOpenPicker;
      delete window.beaconClosePicker;
      delete window.beaconInfoStrip;
      delete window.beaconSimNow;
      delete window.beaconSimReset;
    };
  }, [streamIdByVideoId, streamIdByName, beaconUserEnabled, tinkUserEnabled, hlsVolume, hlsMuted, syncMode.active, gearExpanded, mbStatus, showSchedule]);
  // ===== Cue CSV loading (4b) =====
  useEffect(() => {
    const showId =
      syncMode.anchor?.showId || (syncMode.earlyEntry ? syncMode.earlyEntryShowId : null);
    if (!showId) return;
    if (showId === "fake-test") {
      if (!cuesByShow["fake-test"]) {
        setCuesByShow((prev) => ({ ...prev, "fake-test": [] }));
      }
      return;
    }
    if (cuesByShow[showId] !== undefined) return;

    setCuesByShow((prev) => ({ ...prev, [showId]: "loading" }));
    (async () => {
      try {
        const r = await fetch(`${BEACON_API_BASE}/cues/${showId}.csv`);
        if (!r.ok) {
          console.warn(`[beacon] cues for ${showId} not found (HTTP ${r.status})`);
          setCuesByShow((prev) => ({ ...prev, [showId]: [] }));
          return;
        }
        const text = await r.text();
        const cues = parseCueCsv(text);
        console.log(`[beacon] loaded ${cues.length} cues for ${showId}`);
        setCuesByShow((prev) => ({ ...prev, [showId]: cues }));
      } catch (e) {
        console.warn(`[beacon] cue load failed for ${showId}: ${e.message}`);
        setCuesByShow((prev) => ({ ...prev, [showId]: [] }));
      }
    })();
  }, [syncMode.anchor?.showId, syncMode.earlyEntry, syncMode.earlyEntryShowId, cuesByShow]);
  // ===== Per-session anchor polling (3d + 4a + 4e) =====
  useEffect(() => {
    if (!syncMode.active || !syncMode.streamId) return;
    const sid = syncMode.streamId;

    const handleAnchorUpdate = (newAnchor) => {
      if (!newAnchor) {
        if (syncMode.earlyEntry && !currentAnchorRef.current) {
          return;
        }
        console.log("[beacon] sync: lock cleared upstream, exiting");
        gatedSendCancel(beaconDeviceIdRef.current);
        setSyncMode({
          active: false,
          streamId: null,
          anchor: null,
          earlyEntry: false,
          earlyEntryShowId: null,
          showtimeMs: null,
        });
        return;
      }
      const oldAnchor = currentAnchorRef.current;
      if (oldAnchor && oldAnchor.showId !== newAnchor.showId) {
        console.log(
          `[beacon] sync: show changed ${oldAnchor.showId} → ${newAnchor.showId}`
        );
        gatedSendCancel(beaconDeviceIdRef.current);
      } else if (
        oldAnchor &&
        oldAnchor.anchorShowPosSec != null &&
        newAnchor.anchorShowPosSec != null &&
        oldAnchor.anchorShowPosSec - newAnchor.anchorShowPosSec >
          ANCHOR_BACKWARD_RESET_S
      ) {
        console.log(
          `[beacon] sync: anchor jumped back >${ANCHOR_BACKWARD_RESET_S}s — resetting cursor`
        );
        gatedSendCancel(beaconDeviceIdRef.current);
      } else if (!oldAnchor && syncMode.earlyEntry) {
        console.log(
          `[beacon] early-entry: first lock arrived (show=${newAnchor.showId})`
        );
      }
      setSyncMode((prev) => ({ ...prev, anchor: newAnchor }));
    };

    const poll = async () => {
      if (fakeLocksRef.current[sid]) {
        syncSessionRef.current.consecutiveNullPolls = 0;
        if (graceModeActive) {
          const elapsedMs =
            Date.now() - syncSessionRef.current.graceModeStartedAt;
          console.log(
            `[beacon] sync: grace recovered after ${(elapsedMs / 1000).toFixed(1)}s — resuming`
          );
          setGraceModeActive(false);
          syncSessionRef.current.graceModeStartedAt = 0;
          syncSessionRef.current.graceModeLastShowPos = null;
          syncSessionRef.current.graceModeReferenceDuration = null;
        }
        handleAnchorUpdate(fakeLocksRef.current[sid]);
        return;
      }

      try {
        const r = await fetch(
          `${BEACON_API_BASE}/api/beacon-sync/${encodeURIComponent(sid)}`
        );
        if (!r.ok) return;
        const j = await r.json();
        const lockData = j && j.lock;
        const newAnchor = lockData && lockData.showId ? lockData : null;

        if (newAnchor) {
          syncSessionRef.current.consecutiveNullPolls = 0;
          if (graceModeActive) {
            const elapsedMs =
              Date.now() - syncSessionRef.current.graceModeStartedAt;
            console.log(
              `[beacon] sync: grace recovered after ${(elapsedMs / 1000).toFixed(1)}s — resuming`
            );
            setGraceModeActive(false);
            syncSessionRef.current.graceModeStartedAt = 0;
            syncSessionRef.current.graceModeLastShowPos = null;
            syncSessionRef.current.graceModeReferenceDuration = null;
          }
          handleAnchorUpdate(newAnchor);
        } else {
          if (syncMode.earlyEntry && !currentAnchorRef.current) {
            return;
          }

          syncSessionRef.current.consecutiveNullPolls += 1;

          if (graceModeActive) {
            const elapsedMs =
              Date.now() - syncSessionRef.current.graceModeStartedAt;
            if (elapsedMs >= GRACE_TIMEOUT_MS) {
              console.warn(
                `[beacon] sync: grace timeout (${(elapsedMs / 1000).toFixed(0)}s) — exiting`
              );
              setGraceModeActive(false);
              syncSessionRef.current.graceModeStartedAt = 0;
              syncSessionRef.current.graceModeLastShowPos = null;
              syncSessionRef.current.graceModeReferenceDuration = null;
              gatedSendCancel(beaconDeviceIdRef.current);
              setSyncMode({
                active: false,
                streamId: null,
                anchor: null,
                earlyEntry: false,
                earlyEntryShowId: null,
                showtimeMs: null,
              });
            }
            return;
          }

          if (
            syncSessionRef.current.consecutiveNullPolls >= NULL_POLL_TOLERANCE
          ) {
            const lastShowPos = lastShowPosRef.current;
            const refDuration = currentAnchorRef.current?.referenceDurationSec;
            const wasNearEnd =
              typeof lastShowPos === "number" &&
              typeof refDuration === "number" &&
              refDuration > 0 &&
              refDuration - lastShowPos < WAS_NEAR_END_THRESHOLD_S;

            if (wasNearEnd) {
              console.log("[beacon] sync: clean show-end — exiting");
              handleAnchorUpdate(null);
            } else {
              setGraceModeActive(true);
              syncSessionRef.current.graceModeStartedAt = Date.now();
              syncSessionRef.current.graceModeLastShowPos = lastShowPos;
              syncSessionRef.current.graceModeReferenceDuration = refDuration;
              console.log(
                `[beacon] sync: mid-show unlock — entering ${GRACE_TIMEOUT_MS / 1000}s grace window (silent)`
              );
              gatedSendCancel(beaconDeviceIdRef.current);
            }
          } else {
            console.log(
              `[beacon] sync: null poll ${syncSessionRef.current.consecutiveNullPolls}/${NULL_POLL_TOLERANCE} — waiting for confirmation`
            );
          }
        }
      } catch (e) {
        // swallow
      }
    };

    poll();
    const id = setInterval(poll, SYNC_LOCK_POLL_MS);
    return () => clearInterval(id);
  }, [syncMode.active, syncMode.streamId, syncMode.earlyEntry, graceModeActive]);
  const resolveStreamId = (videoId, streamerName) => {
    let sid = videoId ? streamIdByVideoId[videoId] : null;
    if (!sid && streamerName) {
      sid = streamIdByName[streamerName.toLowerCase()];
    }
    return sid || null;
  };

  const getMagicStateForVideoId = (videoId, streamerName) => {
    if (!beaconUserEnabled) return "none";
    const streamId = resolveStreamId(videoId, streamerName);
    if (!streamId) return "none";
    if (fakeLocks[streamId] || globalAnchors[streamId]) return "proven";
    return "none";
  };

  // ===== 4g: HLS volume routing for vol+/vol-/mute =====
  const persistHlsVol = (vol, muted) => {
    lsSet(LS_HLS_VOL, vol);
    lsSet(LS_HLS_MUTE, muted ? 1 : 0);
  };

  const handleMute = () => {
    if (syncMode.active) {
      const next = !hlsMuted;
      setHlsMuted(next);
      persistHlsVol(hlsVolume, next);
      console.log(`[beacon] HLS mute → ${next}`);
    } else {
      toggleMute();
    }
  };

  const handleVolUp = () => {
    if (syncMode.active) {
      const next = Math.min(100, hlsVolume + VOL_STEP);
      setHlsVolume(next);
      persistHlsVol(next, hlsMuted);
      console.log(`[beacon] HLS volume → ${next}`);
    } else {
      increaseVolume();
    }
  };

  const handleVolDown = () => {
    if (syncMode.active) {
      const next = Math.max(0, hlsVolume - VOL_STEP);
      setHlsVolume(next);
      persistHlsVol(next, hlsMuted);
      console.log(`[beacon] HLS volume → ${next}`);
    } else {
      decreaseVolume();
    }
  };

  const handleBeaconToggle = () => {
    const next = !beaconUserEnabled;
    setBeaconUserEnabled(next);
    lsSet(LS_BEACON_USER_ENABLED, next ? 1 : 0);
    console.log(`[beacon] user toggle → ${next ? "ENABLED" : "DISABLED"}`);
    if (!next && syncMode.active) {
      exitSyncMode("beacon disabled by user");
    }
  };

  const handleTinkToggle = () => {
    const next = !tinkUserEnabled;
    setTinkUserEnabled(next);
    lsSet(LS_TINK_USER_ENABLED, next ? 1 : 0);
    console.log(`[beacon] tink toggle → ${next ? "ENABLED" : "DISABLED"}`);
  };

  // ============================================================
  // 4h: Stream picker — helpers, open/close, click handlers.
  // Mirrors multitest2 line 7587-7975 + helpers.
  // ============================================================

  // getAnyAnchor — fakeLocks first, then globalAnchors. Multitest2 line 2796.
  const getAnyAnchor = (streamId) => {
    if (fakeLocks[streamId]) return fakeLocks[streamId];
    if (globalAnchors[streamId]) return globalAnchors[streamId];
    return null;
  };

  // Locked streams across slots + roster. Multitest2 line 7587.
  // Returns [{ streamId, meta, slotIdx }, ...]; slotIdx is null when
  // the stream is locked in roster but not loaded into a slot yet.
  const getLockedStreams = () => {
    const out = [];
    const seen = new Set();
    // 1. Locked slots first — preserve slotIdx so picker can enter
    //    sync directly without an assignToSlot step.
    for (let i = 0; i < videoIds.length; i++) {
      const vid = videoIds[i];
      if (!vid) continue;
      const streamerName = videoIdToStreamer[vid];
      const streamId = resolveStreamId(vid, streamerName);
      if (!streamId || seen.has(streamId)) continue;
      if (!getAnyAnchor(streamId)) continue;
      const meta = roster.find((r) => r.streamId === streamId);
      if (!meta) continue;
      out.push({ streamId, meta, slotIdx: i });
      seen.add(streamId);
    }
    // 2. Roster entries not already in a slot, picker will auto-assign.
    for (const r of roster) {
      if (!r.streamId || seen.has(r.streamId)) continue;
      if (!getAnyAnchor(r.streamId)) continue;
      out.push({ streamId: r.streamId, meta: r, slotIdx: null });
    }
    return out;
  };

  // Eligible streams for a specific show. Multitest2 line 7630.
  const getEligibleStreamsForShow = (showId) => {
    const out = [];
    const seen = new Set();
    // 1. Slots first
    for (let i = 0; i < videoIds.length; i++) {
      const vid = videoIds[i];
      if (!vid) continue;
      const streamerName = videoIdToStreamer[vid];
      const streamId = resolveStreamId(vid, streamerName);
      if (!streamId || seen.has(streamId)) continue;
      const elig = eligibleStreams[streamId];
      if (!elig || !Array.isArray(elig.eligibleForShows)) continue;
      if (!elig.eligibleForShows.includes(showId)) continue;
      const meta = roster.find((r) => r.streamId === streamId);
      if (!meta) continue;
      out.push({ streamId, meta, slotIdx: i });
      seen.add(streamId);
    }
    // 2. Roster
    for (const r of roster) {
      if (!r.streamId || seen.has(r.streamId)) continue;
      const elig = eligibleStreams[r.streamId];
      if (!elig || !Array.isArray(elig.eligibleForShows)) continue;
      if (!elig.eligibleForShows.includes(showId)) continue;
      out.push({ streamId: r.streamId, meta: r, slotIdx: null });
    }
    return out;
  };

  // Assign a stream to a slot. Used when a picker tile is tapped for a
  // streamId that isn't in any slot yet (slotIdx === null).
  const assignToSlot = (slotIdx, streamId) => {
    const meta = roster.find((r) => r.streamId === streamId);
    if (!meta || !meta.youtubeUrl) return false;
    const vid = extractYouTubeId(meta.youtubeUrl);
    if (!vid) return false;
    setVideoIds((prev) => {
      const next = [...prev];
      next[slotIdx] = vid;
      return next;
    });
    setCurrentVideoIds((prev) => {
      const next = [...prev];
      next[slotIdx] = vid;
      return next;
    });
    return true;
  };

  // Early-entry common path. Multitest2 line 7869.
  const earlyEntryEnterStream = ({ streamId, meta, slotIdx }, showId) => {
    let targetIdx = slotIdx;
    if (targetIdx === null) {
      targetIdx = selectedIndex ?? 0;
      assignToSlot(targetIdx, streamId);
    }
    // Look up showtime from schedule for this show.
    const dates = showSchedule[showId];
    let showtimeMs = null;
    if (Array.isArray(dates) && dates.length > 0) {
      const now = Date.now();
      let best = Infinity;
      for (const d of dates) {
        const ms = d.getTime();
        if (ms > now - 60_000 && ms < best) best = ms;
      }
      if (best !== Infinity) showtimeMs = best;
    }
    // Bug 7 fix: pass streamIdOverride so enterSyncMode doesn't read
    // videoIds[targetIdx] — that read can return the WRONG streamer if
    // the slot was just assigned (React state not flushed yet) or if a
    // different eligible streamer happens to be in that slot. Same root
    // cause as bug 5 (locked-picker wrong HLS). Reported as "5-min HEA
    // CTA opened a Fantasmic-eligible stream's HLS."
    enterSyncModeInternal(targetIdx, {
      earlyEntry: true,
      showId,
      showtimeMs,
      streamIdOverride: streamId,
    });
  };

  // Close picker. Multitest2 line 7880.
  const closeStreamPicker = () => {
    setStreamPicker({
      open: false,
      items: [],
      title: "",
      mode: "locked",
      showId: null,
      showIds: null,
      openedAt: 0,
    });
  };

  // Close picker with the 300ms tap-through guard. Multitest2 line 7900.
  const closeStreamPickerWithGuard = () => {
    if (Date.now() - streamPicker.openedAt < 300) return;
    closeStreamPicker();
  };

  // ----- Open functions -----

  // openStreamPicker — for the green CONNECTED badge tap with 2+ locks.
  // Multitest2 line 7621.
  const openStreamPicker = () => {
    const locked = getLockedStreams();
    if (locked.length === 0) return;
    setStreamPicker({
      open: true,
      items: locked,
      title: "Tap a stream to sync to your band",
      mode: "locked",
      showId: null,
      showIds: null,
      openedAt: Date.now(),
    });
  };

  // openEarlyEntryPicker — single-show countdown CTA. If exactly one
  // stream is eligible, auto-enter (mirrors locked-picker 1-vs-many UX).
  // Multitest2 line 7660.
  const openEarlyEntryPicker = (showId) => {
    const eligible = getEligibleStreamsForShow(showId);
    if (eligible.length === 0) {
      console.warn(
        `[beacon] early-entry picker: no eligible streams for ${showId}`
      );
      return;
    }
    if (eligible.length === 1) {
      console.log(
        `[beacon] early-entry: 1 eligible stream → auto-entering ${eligible[0].streamId}`
      );
      earlyEntryEnterStream(eligible[0], showId);
      return;
    }
    const showName = prettyShow(showId);
    setStreamPicker({
      open: true,
      items: eligible,
      title: `Pick a stream to start watching ${showName} early`,
      mode: "early",
      showId,
      showIds: null,
      openedAt: Date.now(),
    });
  };

  // openEarlyEntryPickerMulti — multi-show countdown CTA. Never auto-
  // picks since customer should see the show choice. Multitest2 line 7689.
  const openEarlyEntryPickerMulti = (showIds) => {
    if (!Array.isArray(showIds) || showIds.length === 0) return;
    const items = [];
    const seen = new Set();
    for (const showId of showIds) {
      for (const s of getEligibleStreamsForShow(showId)) {
        if (seen.has(s.streamId)) continue;
        seen.add(s.streamId);
        items.push({ ...s, pickerShowId: showId });
      }
    }
    if (items.length === 0) {
      console.warn(
        `[beacon] early-entry picker (multi): no eligible streams for ${showIds.join(",")}`
      );
      return;
    }
    const names = showIds.map((s) => prettyShow(s));
    const titleShowStr =
      names.length === 2
        ? `${names[0]} or ${names[1]}`
        : names.slice(0, -1).join(", ") + " or " + names[names.length - 1];
    setStreamPicker({
      open: true,
      items,
      title: `Pick a stream — ${titleShowStr}`,
      mode: "early-multi",
      showId: null,
      showIds: showIds.slice(),
      openedAt: Date.now(),
    });
  };

  // openLockedPickerMulti — multi-show locked CTA. Multitest2 line 7931.
  const openLockedPickerMulti = (showIds) => {
    if (!Array.isArray(showIds) || showIds.length === 0) return;
    const items = [];
    const seen = new Set();
    const allLocked = getLockedStreams();
    for (const showId of showIds) {
      for (const ls of allLocked) {
        if (seen.has(ls.streamId)) continue;
        const anchor = getAnyAnchor(ls.streamId);
        if (!anchor || anchor.showId !== showId) continue;
        seen.add(ls.streamId);
        items.push({ ...ls, pickerShowId: showId });
      }
    }
    if (items.length === 0) return;
    // Auto-enter single result.
    if (items.length === 1) {
      const { streamId, slotIdx } = items[0];
      if (slotIdx !== null) {
        enterSyncMode(slotIdx);
      } else {
        const targetIdx = selectedIndex ?? 0;
        if (assignToSlot(targetIdx, streamId)) {
          // Defer the enterSyncMode until React applies the new videoId.
          setTimeout(() => enterSyncMode(targetIdx), 0);
        }
      }
      return;
    }
    const names = showIds.map((s) => prettyShow(s));
    const titleShowStr =
      names.length === 2
        ? `${names[0]} or ${names[1]}`
        : names.slice(0, -1).join(", ") + " or " + names[names.length - 1];
    setStreamPicker({
      open: true,
      items,
      title: `Pick a stream — ${titleShowStr}`,
      mode: "locked-multi",
      showId: null,
      showIds: showIds.slice(),
      openedAt: Date.now(),
    });
  };

  // Tile click handler.
  const handlePickerTileClick = (item) => {
    closeStreamPicker();
    const { streamId, meta, slotIdx, pickerShowId } = item;
    const earlyEntry =
      streamPicker.mode === "early" || streamPicker.mode === "early-multi";
    if (earlyEntry) {
      const targetShowId = pickerShowId || streamPicker.showId;
      earlyEntryEnterStream({ streamId, meta, slotIdx }, targetShowId);
    } else {
      // Locked path. CRITICAL: enter sync by streamId, NOT by slotIndex.
      // The picker tile carries the streamId of the streamer the user
      // actually tapped. Reading videoIds[slotIdx] at the moment of entry
      // can return a DIFFERENT streamer if liveStreams shuffled slots
      // between picker open and tap — caused the "tap thumbnail → wrong
      // HLS video" bug. enterSyncModeInternal's streamIdOverride bypasses
      // the slot lookup entirely and resolves the videoId/streamId from
      // roster instead.
      if (slotIdx == null) {
        // Stream isn't in any slot yet — auto-assign first (so the slot
        // shows the right embed under the sync overlay), then enter via
        // override so the slot's not-yet-flushed state doesn't matter.
        const targetIdx = selectedIndex ?? 0;
        assignToSlot(targetIdx, streamId);
        setTimeout(
          () =>
            enterSyncModeInternal(targetIdx, { streamIdOverride: streamId }),
          0
        );
        return;
      }
      enterSyncModeInternal(slotIdx, { streamIdOverride: streamId });
    }
  };

  // handleConnectedBadgeTap — entry point used by the mb-status locked
  // CTA. Multitest2 line 7905.
  //   0 locked: do nothing (defensive)
  //   1 locked + in slot:    enter sync directly
  //   1 locked + not in slot: auto-assign then enter sync
  //   2+ locked: open picker
  const handleConnectedBadgeTap = () => {
    const locked = getLockedStreams();
    if (locked.length === 0) return;
    if (locked.length === 1) {
      const { streamId, slotIdx } = locked[0];
      // CRITICAL: enter via streamIdOverride (same fix as picker tile).
      // Reading videoIds[slotIdx] can return the wrong streamer if slots
      // shuffled since the badge state was computed.
      if (slotIdx !== null) {
        enterSyncModeInternal(slotIdx, { streamIdOverride: streamId });
      } else {
        const targetIdx = selectedIndex ?? 0;
        assignToSlot(targetIdx, streamId);
        setTimeout(
          () =>
            enterSyncModeInternal(targetIdx, { streamIdOverride: streamId }),
          0
        );
      }
      return;
    }
    openStreamPicker();
  };

  // 5a-iii / 4h: clicking a locked-cta row in the badge.
  //   - Multi-show row (showIds.length > 1): openLockedPickerMulti
  //   - Single-show: handleConnectedBadgeTap (auto-enter if 1 locked,
  //     picker if 2+, auto-assign-then-enter if not yet in a slot).
  const handleLockedRowClick = (row) => {
    if ((row.showIds || []).length > 1) {
      openLockedPickerMulti(row.showIds);
      return;
    }
    handleConnectedBadgeTap();
  };

  // 5a-iii / 4h: clicking a countdown-cta row in the badge.
  //   - Single show:  openEarlyEntryPicker(showId)  (auto-enter if 1
  //                    eligible streamer)
  //   - Multi-show:   openEarlyEntryPickerMulti(showIds)
  const handleCountdownCtaClick = (row) => {
    const showIds = row.showIds || [];
    if (showIds.length === 0) return;
    if (showIds.length > 1) {
      openEarlyEntryPickerMulti(showIds);
    } else {
      openEarlyEntryPicker(showIds[0]);
    }
  };

  const enterSyncModeInternal = (slotIndex, opts = {}) => {
    if (!beaconUserEnabled) {
      console.log("[beacon] enterSync blocked: Beacon disabled by user");
      return;
    }

    const earlyEntry = !!opts.earlyEntry;
    const earlyEntryShowId = opts.showId || null;
    const showtimeMs = opts.showtimeMs ?? null;

    // 5b: peer-handoff enters directly by streamId (the slot's videoId may
    // not have flushed through React state yet). When streamIdOverride is
    // given, resolve from it instead of reading videoIds[slotIndex].
    let videoId;
    let streamId;
    if (opts.streamIdOverride) {
      streamId = opts.streamIdOverride;
      const meta = roster.find((r) => r.streamId === streamId);
      videoId = meta && meta.youtubeUrl ? extractYouTubeId(meta.youtubeUrl) : null;
      if (!videoId) {
        console.warn(
          "[beacon] cannot enter sync (override): no videoId for streamId",
          streamId
        );
        return;
      }
    } else {
      videoId = videoIds[slotIndex];
      if (!videoId) {
        console.warn("[beacon] cannot enter sync: no videoId for slot", slotIndex);
        return;
      }
      const streamerName = videoIdToStreamer[videoId];
      streamId = resolveStreamId(videoId, streamerName);
      if (!streamId) {
        console.warn("[beacon] cannot enter sync: no streamId for", {
          videoId,
          streamerName,
        });
        return;
      }
    }
    // Resolve the anchor from current state OR the live refs. The refs
    // (fakeLocksRef) are updated synchronously-ish by the ref-sync effect,
    // so a fake lock set moments before entering (e.g. dev helpers, picker
    // auto-assign) is seen here even if this closure's `fakeLocks`/
    // `globalAnchors` state snapshot hasn't flushed yet.
    const anchor =
      fakeLocks[streamId] ||
      globalAnchors[streamId] ||
      (fakeLocksRef.current && fakeLocksRef.current[streamId]) ||
      null;

    if (!earlyEntry && !anchor) {
      console.warn(
        "[beacon] cannot enter sync (locked path): no anchor for streamId",
        streamId
      );
      return;
    }
    if (earlyEntry && !earlyEntryShowId) {
      console.warn("[beacon] cannot enter early-entry sync: no showId");
      return;
    }
    if (syncMode.active) return;

    if (earlyEntry) {
      console.log(
        `[beacon] 🪄 SYNC ENTER (early-entry) — ${streamId} for ${earlyEntryShowId}` +
          (showtimeMs
            ? `, showtime in ${Math.round((showtimeMs - Date.now()) / 1000)}s`
            : "")
      );
    } else {
      console.log("[beacon] 🪄 SYNC ENTER —", streamId);
    }

    syncSessionRef.current = {
      consecutiveNullPolls: 0,
      graceModeStartedAt: 0,
      graceModeLastShowPos: null,
      graceModeReferenceDuration: null,
    };
    setGraceModeActive(false);
    lastShowPosRef.current = null;
    // 4g: collapse the gear when entering sync — multitest2 does the same
    // implicitly by mounting a fresh HLS page. Keeps the UI tidy.
    setGearExpanded(false);
    // Bug 6 fix: add body.mb-doors-active SYNCHRONOUSLY here so embeds get
    // hidden/muted in the same paint as the picker closing. Otherwise
    // there's a 1-frame gap between picker-tile-tap and SyncFullscreen
    // mounting + setting the class — during that gap the just-selected
    // embed unmutes and the customer hears a quick blip of audio.
    // SyncFullscreen will take over managing this class once mounted.
    if (typeof document !== "undefined") {
      document.body.classList.add("mb-doors-active");
    }
    setSyncMode({
      active: true,
      streamId,
      anchor: anchor || null,
      earlyEntry,
      earlyEntryShowId,
      showtimeMs,
    });
  };

  const enterSyncMode = (slotIndex) => enterSyncModeInternal(slotIndex, {});

  // 5f: per-stream badge phase resolver for the embed-fullscreen in-slot
  // badge. Returns { showId, phase, startMs } where phase is one of
  // locked | syncing | countdown | preview (precedence in that order), or
  // null if this stream has no active badge. Mirrors multitest2
  // getActiveBadgeShowForStream (line 5585). Gated by eligibility, the
  // per-show dashboard toggle, schedule, others-locked, and staleness.
  const getActiveBadgeShowForStream = (streamId) => {
    if (!streamId) return null;
    const elig = eligibleStreams?.[streamId];
    if (
      !elig ||
      !Array.isArray(elig.eligibleForShows) ||
      elig.eligibleForShows.length === 0
    )
      return null;
    const eligibleShowSet = new Set(elig.eligibleForShows);
    const now = Date.now();
    const phaseNow = now - MB_COUNTDOWN_DISPLAY_OFFSET_MS;
    const isShowEnabled = (showId) => monitorEnabled[showId] !== false;

    let lockedHit = null;
    let syncingHit = null;
    let countdownHit = null;
    let previewHit = null;

    for (const [showId, dates] of Object.entries(showSchedule || {})) {
      if (!isShowEnabled(showId)) continue;
      if (!eligibleShowSet.has(showId)) continue;
      if (!Array.isArray(dates)) continue;
      const durationSec = MB_SHOW_DURATION_SEC[showId] || 1800;
      for (const d of dates) {
        const startMs = d.getTime();
        const endMs = startMs + durationSec * 1000;
        // Lock state checked BEFORE the endMs cutoff — active locks are
        // authoritative even past expected duration.
        const ownAnchor =
          globalAnchors[streamId] || fakeLocks[streamId] || null;
        const isLockedHere = ownAnchor && ownAnchor.showId === showId;
        if (isLockedHere) {
          if (!lockedHit) lockedHit = { showId, phase: "locked", startMs };
          continue;
        }
        if (now > endMs) continue;
        // Skip if SOMEONE ELSE is locked for this show — they handle it.
        let othersLocked = false;
        for (const [sid, anchor] of Object.entries(globalAnchors || {})) {
          if (sid === streamId) continue;
          if (anchor && anchor.showId === showId) {
            othersLocked = true;
            break;
          }
        }
        if (othersLocked) continue;
        const timeToStartMs = startMs - phaseNow;
        // COUNTDOWN: pre-show, within 5 min of phase-adjusted start.
        if (timeToStartMs > 0 && timeToStartMs <= MB_COUNTDOWN_THRESHOLD_MS) {
          if (!countdownHit)
            countdownHit = { showId, phase: "countdown", startMs };
          continue;
        }
        // SYNCING: showtime hit, before show ends, within hard-cap.
        if (timeToStartMs <= 0 && now <= endMs) {
          const sinceShowtime = now - startMs;
          const phases = getSyncingPhases(showId);
          const hardCap = phases.initialMs + 2 * phases.extendedMs;
          if (sinceShowtime > hardCap) continue;
          if (!syncingHit) syncingHit = { showId, phase: "syncing", startMs };
          continue;
        }
        // PREVIEW: pre-showtime, outside the 5-min window.
        if (timeToStartMs > MB_COUNTDOWN_THRESHOLD_MS) {
          if (!previewHit) previewHit = { showId, phase: "preview", startMs };
          continue;
        }
      }
    }
    // Precedence: locked > syncing > countdown > preview.
    return lockedHit || syncingHit || countdownHit || previewHit;
  };

  // 5b: find a peer streamer already locked on the same show. Mirrors
  // multitest2 findLockedPeer (line 2825). Reads globalAnchors + fakeLocks;
  // intentionally does NOT require isProven — an anchor on the matching
  // show is enough to offer the handoff. Excludes the current stream.
  const findLockedPeer = (showId, excludeStreamId) => {
    if (!showId) return null;
    const all = { ...globalAnchors, ...fakeLocks };
    for (const sid of Object.keys(all)) {
      if (sid === excludeStreamId) continue;
      const a = all[sid];
      if (!a || a.showId !== showId) continue;
      // Quick-swap health filter (2026-07-14): skip peers whose recent
      // delivery p90 is in the starvation zone. Fail-open when segHealth
      // is absent (unknown is not unhealthy).
      if (a.segHealth && a.segHealth.p90Ms >= 4000) continue;
      const meta = roster.find((s) => s.streamId === sid);
      return { streamId: sid, name: (meta && meta.name) || sid };
    }
    return null;
  };

  // 5b: execute a peer handoff. The viewer is in syncMode on one stream
  // (waiting/syncing) and taps the gold bubble to jump to a peer already
  // locked on the same show. Mirrors multitest2 peerHandoff (line 2848).
  //
  // Sequence: exit the current session cleanly (tears down HLS, doors,
  // BLE, warning UI), point this slot at the peer, then re-enter via the
  // NORMAL locked path (NOT earlyEntry — the peer already has an anchor,
  // so there's no lock event to celebrate; lands directly in calm green
  // locked state). Because exitSyncMode flips syncMode.active async and
  // enterSyncModeInternal bails while active, the slot assign + re-enter
  // are deferred to the next tick via the peerHandoffPendingRef (declared
  // above with the other refs), which the sync-exit effect picks up once
  // syncMode.active has cleared.

  const peerHandoff = (peerStreamId) => {
    if (!peerStreamId) return;
    // If the peer is ALREADY displayed in a slot, target that slot (no
    // reassign → no duplicate). Otherwise use the current slot + assign it.
    let targetIdx = -1;
    for (let i = 0; i < videoIds.length; i++) {
      const vid = videoIds[i];
      if (!vid) continue;
      const sid = resolveStreamId(vid, videoIdToStreamer[vid]);
      if (sid === peerStreamId) {
        targetIdx = i;
        break;
      }
    }
    const alreadyInSlot = targetIdx !== -1;
    const idx = alreadyInSlot
      ? targetIdx
      : fullscreenIndex != null
      ? fullscreenIndex
      : selectedIndex;
    const all = { ...globalAnchors, ...fakeLocks };
    const peerAnchor = all[peerStreamId];
    const peerShowId = (peerAnchor && peerAnchor.showId) || null;
    console.log(
      `[beacon] peer-handoff: → ${peerStreamId} (slot ${idx}${
        alreadyInSlot ? ", already displayed" : ", assigning"
      }, show ${peerShowId})`
    );

    // Assign the slot for visual consistency on later exit (only if the
    // peer isn't already in a slot — assigning otherwise duplicates it).
    if (!alreadyInSlot) {
      assignToSlot(idx, peerStreamId);
    }

    // No explicit CANCEL here — the fresh SyncFullscreen mount fires
    // CANCEL immediately on entry (its doors-BLE init, SyncFullscreen.jsx
    // line 569). The new instance's doors then run their own
    // CANCEL → WAKE_UP_FAST × N → CANCEL ceremony, fully clearing any
    // in-flight effect from the previous stream. An extra CANCEL here
    // would just duplicate the first one ~50ms before it fires.

    // SWAP IN ONE UPDATE — do NOT exitSyncMode first. exitSyncMode sets
    // active:false which unmounts SyncFullscreen → embed page paints for a
    // frame (the flash) → re-enter remounts it. Instead we keep active:true
    // and just change the streamId/anchor. SyncFullscreen is keyed by
    // streamId, so React unmounts the old + mounts the new in a SINGLE
    // commit: doors run fresh for the peer, no embed-page flash between.
    setGraceModeActive(false);
    lastShowPosRef.current = null;
    setSyncMode({
      active: true,
      streamId: peerStreamId,
      anchor: peerAnchor || null,
      earlyEntry: false, // peer is already locked → calm locked entry
      earlyEntryShowId: null,
      showtimeMs: null,
    });
  };

  // 5b: completes a pending peer handoff once sync mode has torn down.
  // (Retained for the not-in-sync path; the in-sync path now swaps
  // directly in peerHandoff without a teardown.)
  const flushPeerHandoff = () => {
    const pending = peerHandoffPendingRef.current;
    if (!pending) return;
    peerHandoffPendingRef.current = null;
    const { idx, peerStreamId, alreadyInSlot } = pending;
    if (!alreadyInSlot) {
      assignToSlot(idx, peerStreamId);
    }
    enterSyncModeInternal(idx, { streamIdOverride: peerStreamId });
  };

  const exitSyncMode = (reason) => {
    // Defensive: clear any pending peer-handoff before exiting. The flush
    // effect below watches syncMode.active flipping false AND
    // peerHandoffPendingRef.current being set — if a stale pending ever
    // exists (legacy code path, future change, edge case), it would
    // re-enter sync during a user-initiated exit and leave the app stuck.
    // Clearing here neutralizes that entire class of bug. In normal use
    // the ref is already null and this is a no-op.
    peerHandoffPendingRef.current = null;
    if (!syncMode.active) return;
    console.log("[beacon] sync exit:", reason);
    // Bug 8 fix: BLE exit ceremony — flush in-flight effect, hold all
    // lights off, final release. Multitest2 line 3959-3987:
    //   CANCEL → 2s → SOLID_OFF → 2s → CANCEL
    // The cue runner may have left LEDs on the band. SOLID_OFF kills
    // them; bookend CANCELs ensure clean state regardless of what was
    // in flight. Fires async — customer sees the UI return immediately
    // while the sequence plays on the band over ~4 seconds.
    //
    // Exception: 'doors error close' skips the BLE entirely. The error
    // path already fired CANCEL when the error was detected, and
    // nothing has lit on the band since. More BLE would just add noise.
    const deviceId = beaconDeviceIdRef.current;
    if (reason === "doors error close") {
      console.log(
        "[beacon] exit (doors error close): no BLE — band already cleaned"
      );
    } else if (reason === "peer handoff") {
      // Peer handoff doesn't need the full off-ceremony — the new stream
      // is about to start firing fresh cues immediately. SOLID_OFF here
      // would briefly turn the band dark for 2s mid-show. Just a single
      // CANCEL to flush any in-flight effect from the old stream; the
      // new stream's cue runner takes over from there.
      if (deviceId) gatedSendBeaconCmd(deviceId, "CANCEL", "OFF");
    } else if (deviceId) {
      gatedSendBeaconCmd(deviceId, "CANCEL", "OFF");
      setTimeout(() => gatedSendBeaconCmd(deviceId, "SOLID_OFF", "OFF"), 2000);
      setTimeout(() => gatedSendBeaconCmd(deviceId, "CANCEL", "OFF"), 4000);
    }
    setSyncMode({
      active: false,
      streamId: null,
      anchor: null,
      earlyEntry: false,
      earlyEntryShowId: null,
      showtimeMs: null,
    });
    setGraceModeActive(false);
    lastShowPosRef.current = null;
    setGearExpanded(false);
    // UX fix: when user exits HLS sync, return them to the grid view.
    // Auto-promote may have landed them in fullscreen+sync, and exiting
    // ONLY the sync layer leaves them stuck in the fullscreen embed —
    // visually identical to "still synced" which is confusing. Mirrors
    // the Auto Unlock UX where exit goes all the way back to the grid.
    //
    // Optional: only fire if the consumer wired the setter. Falls back
    // to legacy behavior (stays in fullscreen) if undefined.
    if (typeof setFullscreenIndex === "function") {
      setFullscreenIndex(null);
    }
  };

  // 5b: once sync mode has fully torn down (syncMode.active → false) and a
  // peer handoff is pending, complete it. This is the deferred half of
  // peerHandoff — exitSyncMode flips active async, so we wait for the flip
  // here rather than assuming it happened synchronously.
  useEffect(() => {
    if (!syncMode.active && peerHandoffPendingRef.current) {
      flushPeerHandoff();
    }
  }, [syncMode.active]);
  // ===== Derived values for SyncFullscreen wiring =====

  // syncStreamerName — resolve the synced streamId back to its display
  // name for the @label in SyncFullscreen. Tries the videoId map first
  // (will hit if the stream is in a slot), falls back to the name map
  // (will hit if it's in the roster but no slot). Empty string if not in
  // sync mode or not found.
  const syncStreamerName = (() => {
    if (!syncMode.active) return "";
    for (const [vid, sid] of Object.entries(streamIdByVideoId)) {
      if (sid === syncMode.streamId) return videoIdToStreamer[vid] || "";
    }
    for (const [nm, sid] of Object.entries(streamIdByName)) {
      if (sid === syncMode.streamId) return nm;
    }
    return "";
  })();

  // syncCues — the loaded cue array for the currently synced show.
  // Returns [] if not in sync, no show id, cues not loaded yet, or the
  // "loading" sentinel is set. SyncFullscreen treats [] as no cues.
  const syncCues = (() => {
    if (!syncMode.active) return [];
    const showId =
      syncMode.anchor?.showId ||
      (syncMode.earlyEntry ? syncMode.earlyEntryShowId : null);
    if (!showId) return [];
    const entry = cuesByShow[showId];
    return Array.isArray(entry) ? entry : [];
  })();

  // handleShowPosTick — SyncFullscreen reports its current cue-runner
  // showPos (seconds) on every tick. We stash it in a ref so the anchor
  // poller can decide between "clean show-end" vs "mid-show unlock" when
  // the upstream lock vanishes (multitest2 line 7286).
  const handleShowPosTick = (showPos) => {
    lastShowPosRef.current = showPos;
  };

  // ===== Return =====
  //
  // Consumer (Display or TV) reads state for rendering, calls actions
  // for event handlers, and wires the BLE wrappers + handleShowPosTick
  // + findLockedPeer + peerHandoff into <SyncFullscreen> as props.
  return {
    // ---- State (read-only) ----
    syncMode,
    mbStatus,
    infoStrip,
    fsBadges,
    streamPicker,
    graceModeActive,
    hlsVolume,
    hlsMuted,
    beaconUserEnabled,
    tinkUserEnabled,
    gearExpanded,
    globalAnchors,
    fakeLocks,
    cuesByShow,
    streamIdByVideoId,
    streamIdByName,
    roster,
    // ---- Derived ----
    videoIdToStreamer, // Display JSX reads it for slot labels/badges
    syncStreamerName,
    syncCues,
    // ---- Helpers (functions JSX calls during render) ----
    resolveStreamId,
    getMagicStateForVideoId,
    getAnyAnchor,
    // ---- Action functions (event handlers) ----
    setGearExpanded,
    handleMute,
    handleVolUp,
    handleVolDown,
    handleBeaconToggle,
    handleTinkToggle,
    handleLockedRowClick,
    handleCountdownCtaClick,
    openEarlyEntryPicker,
    handleConnectedBadgeTap,
    closeStreamPicker,
    closeStreamPickerWithGuard,
    handlePickerTileClick,
    enterSyncMode,
    exitSyncMode,
    peerHandoff,
    // ---- SyncFullscreen wiring ----
    // SyncFullscreen receives the GATED wrappers, so its CSV cue runner,
    // doors sequence, exit ceremony, etc. all short-circuit when this
    // device isn't the controller. SyncFullscreen sees identical
    // function signatures — no change required there.
    findLockedPeer,
    onShowPosTick: handleShowPosTick,
    sendBeaconCmd: gatedSendBeaconCmd,
    sendCancel: gatedSendCancel,
    beaconDeviceId,
    // ---- Beacon control state for SyncFullscreen's take-control badge ----
    isController,
    hasHolder,
    holder,
    controlSynced,
    controlKeyReady,
    claimControl,
  };
}
